import React, { Fragment, useState } from 'react';
import { Modal, ModalHeader, ModalBody } from '../modal/Modal';
import Tooltip from '../tooltip/Tooltip';
import { useAskEmmaQuestions } from './hooks/useAskEmmaQuestions';
import './ReportJsonViewer.scss';
import GLOBAL from "../../global";
import { downloadReportOnCurrentTab } from '../../utils/Utils';
import { useTenantFeatures } from "../../features/tenantFeatures/useTenantFeature";

const ReportJsonViewer = ({ open, reportData, isLoading, toggleModal }) => {
    const [modalFullScreen, setModalFullScreen] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const { askEmmaQuestion } = useAskEmmaQuestions();

    const { isAthleteEnabled, isSpinealignEnabled } = useTenantFeatures();
    // const IsHtmlReportEnable = (GLOBAL.TENANT_FEATURES?.find(f => f.Name === 'IsHtmlReportEnable')?.Value || 'False') === 'True';

    // Default questions for Postural Abnormalities section
    // In the future, these can be fetched from an API
    const [postureQuestions] = useState([
        "What is Postural Abnormalities Checklist?",
        "How does this affect me?",
        "What should I do about my posture issues?",
        "How can I improve my posture?"
    ]);

    const onClickToggleModalScreenSize = (e) => {
        e.preventDefault();
        setModalFullScreen(!modalFullScreen);
    };

    const getFullScreenModalStyle = () => {
        if (modalFullScreen) {
            return 'modal-full-screen';
        }
        return '';
    };

    const handleDownloadReport = async () => {
        if (!reportData?.Message?.ReportUrl || isDownloading) return;

        setIsDownloading(true);
        try {
            await downloadReportOnCurrentTab(reportData.Message.ReportUrl);
        } catch (error) {
            console.error('Download failed:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    // Extract data only if not loading and data is available
    const Message = reportData?.Message;
    const PatientInfo = Message?.PatientInfo;
    const AssessmentType = Message?.AssessmentType;
    const AssessmentDate = Message?.AssessmentDate;
    const Narratives = Message?.Narratives;
    const ReportHeading = Message?.ReportHeading;
    // Override report title for PBN (PostureBackNeck) when IsSpinealignEnabled is true
    const displayReportHeading = isSpinealignEnabled 
        ? 'PostureBackNeck Assessment Report' 
        : ReportHeading;
    const PostureGradingScales = Message?.PostureGradingScales;
    const RomDataView = Message?.RomDataView;
    const isFollowup = Message?.IsFollowup || false;
    const EvidenceData = Message?.EvalEvidenceData?.EvidenceData;
    const ExerciseList = Message?.ExerciseList;
    const ExercisePlan = Message?.ExercisePlan;
    const RuleBasedData = Message?.RuleBasedData;
    const RiskFactorData = Message?.RiskFactorData;
    const DisclaimerList = Message?.DisclaimerList;
    const GptReportFaqTooltips = Message?.GptReportFaqTooltips;
    const AthleteSignificantChange = Message?.AthleteSignificantChange || Narratives?.find(n => n.Title === 'AthleteSignificantChange')?.Content;

    // Tenant Configuration - determine tenant type from tenant feature flags
    const getTenantType = () => {
        // Check tenant feature flags
        if (isAthleteEnabled) {
            return 'athlete';
        }
        if (isSpinealignEnabled) {
            return 'spinealign';
        }
        // Check for specific tenants
        if (GLOBAL.TENANT === 'vatb') {
            return 'vatb';
        }
        // Default to standard medical
        return 'standard';
    };

    const tenantType = getTenantType();

    // VATB narrative title categories
    const VATB_ASSESSMENT_SUMMARY_TITLES = ['ChiefComplaint', 'HistoryPresentIllness', 'PersonalMedicalHistory', 'FamilyMedicalHistory', 'ObjectivePhysicalFindings', 'SummaryKeyFindings'];
    const VATB_SUBJECTIVE_OBJECTIVE_TITLES = [];
    const VATB_NARRATIVE_ORDER = ['Plan', 'Modality', 'Goal', 'Ergonomics'];
    const VATB_EXERCISE_NARRATIVE_TITLE = 'Exercise';

    // Helper functions for VATB narrative filtering
    const getVatbAssessmentNarratives = (narratives) =>
        narratives?.filter(n => VATB_ASSESSMENT_SUMMARY_TITLES.includes(n.Title) && n.Content) || [];

    const getVatbSubjectiveObjectiveNarratives = (narratives) =>
        narratives?.filter(n => VATB_SUBJECTIVE_OBJECTIVE_TITLES.includes(n.Title) && n.Content) || [];

    const getVatbOtherNarratives = (narratives) =>
        narratives?.filter(n => !VATB_ASSESSMENT_SUMMARY_TITLES.includes(n.Title) &&
            !VATB_SUBJECTIVE_OBJECTIVE_TITLES.includes(n.Title) &&
            n.Title !== VATB_EXERCISE_NARRATIVE_TITLE && n.Content) || [];

    const getSortedVatbNarratives = (narratives) => {
        return [...narratives].sort((a, b) => {
            const indexA = VATB_NARRATIVE_ORDER.indexOf(a.Title);
            const indexB = VATB_NARRATIVE_ORDER.indexOf(b.Title);
            if (indexA === -1 && indexB === -1) return 0;
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });
    };

    // Tenant-specific configuration
    const getTenantConfig = () => {
        const configs = {
            athlete: {
                brandName: 'ATLAS ATHLETE',
                brandTagline: 'AI-guided movement & performance insights',
                profileSectionTitle: 'I. ATHLETE PROFILE',
                movementSectionTitle: 'II. MOVEMENT INSIGHTS & PEER COMPARISON',
                exerciseSectionTitle: (isFollowup && AthleteSignificantChange) ? 'VI. RECOMMENDED EXERCISE PROGRAM' : 'V. RECOMMENDED EXERCISE PROGRAM',
                showEvidenceList: false,
                primaryColor: '#0066A1',
                patientLabel: 'ATHLETE',
                // Athlete-specific display flags
                showPainAssessment: false,
                showRiskFactors: false,
                showAskEmma: false,
                showStrengthTesting: true,
                showWhyItMatters: true,
                showPerformanceProgression: true,
                showSafetyAlerts: true,
                showKeyFindings: true,
                showLegalDisclaimer: true
            },
            spinealign: {
                brandName: 'SPINEALIGN',
                brandTagline: 'Posture & Spinal Health Assessment',
                profileSectionTitle: 'I. PATIENT INFORMATION',
                movementSectionTitle: 'II. POSTURE & MOVEMENT ANALYSIS',
                exerciseSectionTitle: 'APPENDIX SECTION',
                showEvidenceList: true,
                primaryColor: '#19A04F',
                patientLabel: 'PATIENT',
                // Standard display flags
                showPainAssessment: true,
                showRiskFactors: true,
                showAskEmma: true,
                showStrengthTesting: false,
                showWhyItMatters: false,
                showPerformanceProgression: false,
                showSafetyAlerts: false,
                showKeyFindings: false,
                showLegalDisclaimer: false
            },
            vatb: {
                brandName: 'MY MEDICAL HUB',
                brandTagline: 'AI-guided movement & performance insights',
                profileSectionTitle: 'PROFILE',
                movementSectionTitle: 'REGION: Posture',
                exerciseSectionTitle: 'RELATED INTERVENTIONS- EXERCISE PROGRAM',
                showEvidenceList: true,
                primaryColor: '#1176B4',
                patientLabel: 'CLIENT NAME',
                // VATB display flags
                showPainAssessment: true,
                showRiskFactors: true,
                showAskEmma: true,
                showStrengthTesting: false,
                showWhyItMatters: false,
                showPerformanceProgression: false,
                showSafetyAlerts: false,
                showKeyFindings: false,
                showLegalDisclaimer: false,
                showOccupation: true
            },
            standard: {
                brandName: 'MY MEDICAL HUB',
                brandTagline: 'Comprehensive Musculoskeletal Assessment',
                profileSectionTitle: 'I. PATIENT INFORMATION',
                movementSectionTitle: 'II. CLINICAL FINDINGS',
                exerciseSectionTitle: 'RECOMMENDED EXERCISES',
                showEvidenceList: true,
                primaryColor: '#1176B4',
                patientLabel: 'PATIENT',
                // Standard display flags
                showPainAssessment: true,
                showRiskFactors: true,
                showAskEmma: true,
                showStrengthTesting: false,
                showWhyItMatters: false,
                showPerformanceProgression: false,
                showSafetyAlerts: false,
                showKeyFindings: false,
                showLegalDisclaimer: false
            }
        };
        return configs[tenantType] || configs.standard;
    };

    const config = getTenantConfig();

    // Helper function to get narrative section title based on narrative type
    const getNarrativeSectionTitle = (narrativeTitle) => {
        const titleMaps = {
            athlete: {
                'AthleteGeneralFindings': 'III. KEY FINDINGS THAT MAY AFFECT PERFORMANCE',
                'AthleteSummaryPerformanceImpact': 'IV. SUMMARY & PERFORMANCE IMPACT',
                'AthleteRecommendedTrainingProgram': (isFollowup && AthleteSignificantChange) ? 'VI. RECOMMENDED EXERCISE PROGRAM' : 'V. RECOMMENDED EXERCISE PROGRAM',
                'PosturalScreening': 'POSTURAL SCREENING',
                'PosturalChiefComplaint': 'CHIEF COMPLAINTS',
                'Goal': 'GOALS',
                'Modality': 'MODALITY OPTIONS',
                'Plan': 'TREATMENT PLAN',
                'OtherConsideration': 'OTHER CONSIDERATIONS',
                'Ergonomics': 'ERGONOMIC SUGGESTIONS',
                'ExerciseHeader': 'EXERCISE PROGRAM',
                'ExerciseSummary': 'EXERCISE SUMMARY',
                'AthleteInjuryRiskAnalysis': 'INJURY RISK ANALYSIS',
                'AthletePerformanceOptimization': 'PERFORMANCE OPTIMIZATION',
                'AthleteCoverLetter': 'ASSESSMENT OVERVIEW',
                'AthleteProtocolPrompt': 'RECOMMENDED PROTOCOL'
            },
            spinealign: {
                'Plan': 'ADDITIONAL TREATMENT OPTIONS',
                'Goal': 'TREATMENT GOALS',
                'Modality': 'MODALITY OPTIONS',
                'Ergonomics': 'INTERVENTIONS',
                'ChiefComplaint': 'CHIEF COMPLAINT',
                'HistoryPresentIllness': 'HISTORY OF PRESENT ILLNESS',
                'PersonalMedicalHistory': 'PERSONAL MEDICAL HISTORY',
                'FamilyMedicalHistory': 'FAMILY MEDICAL HISTORY',
                'ObjectivePhysicalFindings': 'OBJECTIVE PHYSICAL FINDINGS',
                'SummaryKeyFindings': 'SUMMARY OF KEY FINDINGS'
            },
            vatb: {
                'Plan': 'CARE PLAN OPTIONS',
                'Goal': 'SHORT & LONG-TERM GOALS',
                'Modality': 'MODALITY OPTIONS',
                'Ergonomics': 'ERGONOMIC SUGGESTIONS',
                'ChiefComplaint': 'Chief Complaint',
                'HistoryPresentIllness': 'History of the Present Illness',
                'PersonalMedicalHistory': 'Personal Medical History',
                'FamilyMedicalHistory': 'Family Medical History',
                'ObjectivePhysicalFindings': 'Objective Physical Findings',
                'SummaryKeyFindings': 'Assessment Summary Note'
            },
            standard: {
                'Plan': 'CARE PLAN OPTIONS',
                'Goal': 'GOALS',
                'Modality': 'MODALITY OPTIONS',
                'Ergonomics': 'ERGONOMICS/INTERVENTIONS',
                'ChiefComplaint': 'CHIEF COMPLAINT',
                'HistoryPresentIllness': 'HISTORY OF PRESENT ILLNESS',
                'PersonalMedicalHistory': 'PERSONAL MEDICAL HISTORY',
                'FamilyMedicalHistory': 'FAMILY MEDICAL HISTORY',
                'ObjectivePhysicalFindings': 'OBJECTIVE PHYSICAL FINDINGS',
                'SummaryKeyFindings': 'SUMMARY OF KEY FINDINGS'
            }
        };

        const map = titleMaps[tenantType] || titleMaps.standard;
        return map[narrativeTitle] || narrativeTitle.replace(/([A-Z])/g, ' $1').trim().toUpperCase();
    };

    // Helper function to render a specific narrative by title
    const renderNarrativeByTitle = (narrativeTitle) => {
        if (!Narratives) return null;
        const narrative = Narratives.find(n => n.Title === narrativeTitle && n.Content);
        if (!narrative) return null;

        return (
            <div className="narrative-section">
                <h2>{getNarrativeSectionTitle(narrative.Title)}</h2>
                <div
                    className="narrative-content"
                    dangerouslySetInnerHTML={{ __html: narrative.Content }}
                />
            </div>
        );
    };

    // Helper function to render Key Findings section (RiskFactorData-based)
    const renderKeyFindingsSection = () => {
        if (!config.showKeyFindings || !RiskFactorData?.RiskFactoryByGroupActivities || RiskFactorData.RiskFactoryByGroupActivities.length === 0) {
            return null;
        }

        return (
            <div className="key-findings-section content-wrapper">
                <div className="key-findings-content">
                    {RiskFactorData.RiskFactoryByGroupActivities.map((riskGroup, groupIndex) => (
                        <div key={groupIndex} className="key-findings-group">
                            {riskGroup.RiskGroupName && (
                                <h3 className="key-findings-category-heading" style={{ color: '#0066A1', fontWeight: 'bold', fontSize: '1.2em', marginTop: '20px', marginBottom: '10px' }}>
                                    {riskGroup.RiskGroupName}
                                </h3>
                            )}
                            <ol className="key-findings-list" style={{ marginLeft: '20px', marginBottom: '15px' }}>
                                {riskGroup.AssessmentActivities && riskGroup.AssessmentActivities.map((item, itemIndex) => (
                                    <li key={itemIndex} data-id={item.QuestionId} style={{ marginBottom: '5px' }}>
                                        <span>{item.QTitle}</span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Helper function to render Ask EMMA questions for a section based on Id
    const renderAskEmmaQuestions = (id, sectionTitle) => {
        // Check if feature flag is enabled
        // if (!IsHtmlReportEnable) {
        //     console.log('GptReportFaqTooltips feature is disabled');
        //     return null;
        // }

        // Check if data is available
        if (!GptReportFaqTooltips) {
            console.log('GptReportFaqTooltips not available');
            return null;
        }

        // Find section by Id and check IsActive
        const faqItem = GptReportFaqTooltips.find(
            item => item.IsActive && item.Id === id
        );

        if (!faqItem || !faqItem.TooltipList || faqItem.TooltipList.length === 0) return null;

        return (
            <div className="ask-emma-questions">
                <div className="questions-header">
                    <h4>Ask EMMA — {sectionTitle}</h4>
                    <div className="header-separator"></div>
                </div>
                <div className="questions-pills">
                    {faqItem.TooltipList.map((question, index) => (
                        <button
                            key={index}
                            className="question-pill-btn"
                            onClick={() => handleAskEmmaQuestion(question)}
                        >
                            {question}
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    // Helper function to render posture images (matching athlete PDF structure)
    const renderPostureImages = () => {
        if (!RomDataView?.RomDataImageViewList) return null;

        const postureRegion = RomDataView.RomDataImageViewList.find(r => r.BodyRegionId === 5);
        const postureImages = postureRegion?.DataModel?.filter(m =>
            m.IsDisplayImage &&
            m.RenderImageUrlCompress &&
            !m.RenderImageUrlCompress.includes('UnableToPerform')
        ) || [];

        if (postureImages.length === 0) return null;

        // Get front view (Spine Align) and side view images
        const frontView = postureImages.find(img => img.ReportGridTitle === 'Spine Align' || img.ExerciseName?.includes('Front'));
        const sideView = postureImages.find(img => img.ReportGridTitle === 'Shoulders Rolled Forward' || img.ExerciseName?.includes('Side'));

        return (
            <div className="posture-images-section content-wrapper">
                <div className="posture-images-grid">
                    {/* Front View */}
                    {frontView && (
                        <div className="posture-image-item">
                            <div className="image-label-top">Posture Front View</div>
                            <div className="posture-legend">
                                <div className="legend-row">
                                    <span className="legend-item"><span className="legend-dot" style={{ backgroundColor: '#666' }}></span> MIDLINE DEVIATION</span>
                                    <span className="legend-item"><span className="legend-dot" style={{ backgroundColor: '#dc3545' }}></span> HIP HEIGHT</span>
                                </div>
                                <div className="legend-row">
                                    <span className="legend-item"><span className="legend-dot" style={{ backgroundColor: '#28a745' }}></span> SHOULDER HEIGHT</span>
                                    <span className="legend-item"><span className="legend-dot" style={{ backgroundColor: '#ffc107' }}></span> KNEE HEIGHT</span>
                                </div>
                            </div>
                            <div className="image-wrapper">
                                <img
                                    src={frontView.RenderImageUrlCompress}
                                    alt="Posture Front View"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.parentElement.innerHTML = '<div class="image-error">Image not available</div>';
                                    }}
                                />
                            </div>
                            <div className="image-caption">Spine Alignment Grid (4"x8")</div>
                        </div>
                    )}
                    {/* Side View */}
                    {sideView && (
                        <div className="posture-image-item">
                            <div className="image-label-top">Posture Side View</div>
                            <div className="posture-legend">
                                <div className="legend-row">
                                    <span className="legend-item"><span className="legend-dot" style={{ backgroundColor: '#666' }}></span> MIDLINE DEVIATION</span>
                                </div>
                            </div>
                            <div className="image-wrapper">
                                <img
                                    src={sideView.RenderImageUrlCompress}
                                    alt="Posture Side View"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.parentElement.innerHTML = '<div class="image-error">Image not available</div>';
                                    }}
                                />
                            </div>
                            <div className="image-caption">Posture Analysis Grid (4"x8")</div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Helper function to render posture grading table (matching athlete PDF format)
    const renderPostureGradingTable = () => {
        if (!RomDataView?.RomDataImageViewList) return null;

        const postureRegion = RomDataView.RomDataImageViewList.find(r => r.BodyRegionId === 5);
        const postureData = postureRegion?.DataModel || [];
        // PostureRomDataView is nested inside RomDataView, not Message
        const postureRomData = RomDataView?.PostureRomDataView;

        const postureRegionNew = RomDataView.RomReportDataView.find(r => r.BodyRegionId === 5);
        const postureDataNew = postureRegionNew?.BodyPartList || [];
        // PostureRomDataView is nested inside RomDataView, not Message
        const postureRomDataNew = postureDataNew?.find(m => m.BodyPartName === 'Posture')?.Data;

        const spineAlign = postureRomDataNew.find(m => m.ReportGridTitle === 'Spine Align');
        const forwardHead = postureRomDataNew.find(m => m.ReportGridTitle === 'Forward Head Carriage');
        const shouldersRolled = postureRomDataNew.find(m => m.ReportGridTitle === 'Shoulders Rolled Forward');
        const shoulderHeightDiff = postureRomDataNew.find(m => m.ReportGridTitle === 'Shoulder Height Difference');

        // Get grading scale values from JSON (first scale is for deviations)
        const gradingScale = PostureGradingScales?.[0] || {};
        const normal = `${gradingScale.Excellent}` || '0.50" or less';
        const mild = `${gradingScale.Good}"` || '0.51"-1.00"';
        const moderate = `${gradingScale.Average}"` || '1.01"-1.50"';
        const severe = `${gradingScale.Poor}"` || '1.51"-2.00"';
        const verySevere = `${gradingScale.VeryPoor}` || '2.01" or greater';

        // For vatb tenant, show only POSTURE and deviation columns
        const showGradingColumns = tenantType !== 'vatb';

        // Determine if we should use left or right deviation values based on spine align
        // If L_ROM > R_ROM, use left deviation values, otherwise use right
        const useLeftDeviation = (spineAlign?.L_ROM != null && spineAlign?.R_ROM != null)
            ? spineAlign.L_ROM > spineAlign.R_ROM
            : true; // default to left if not available

        // Helper function to format deviation values (handles both number and string types)
        const formatDeviation = (value) => {
            if (value == null) return '-';
            if (typeof value === 'number') return value.toFixed(2);
            return value; // string values already formattedReport
        };

        return (
            <div className="posture-grading-section content-wrapper">
                <table className="posture-grading-table">
                    <thead>
                        <tr>
                            {showGradingColumns && (
                                <th colSpan="6" className="grading-scale-header">POSTURE GRADING SCALE</th>
                            )}
                            <th colSpan={showGradingColumns ? "3" : "4"} className="current-deviation-header">CURRENT DEVIATION: {AssessmentDate}</th>
                        </tr>
                        <tr>
                            <th>POSTURE</th>
                            {showGradingColumns && (
                                <>
                                    <th>Normal</th>
                                    <th>Mild</th>
                                    <th>Moderate</th>
                                    <th>Severe</th>
                                    <th>Very Severe</th>
                                </>
                            )}
                            <th>L-Deviation</th>
                            <th>Deviation</th>
                            <th>R-Deviation</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Spine Align</td>
                            {showGradingColumns && (
                                <>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                </>
                            )}
                            <td>{spineAlign?.L_ROM != null ? spineAlign.L_ROM : '-'}</td>
                            <td></td>
                            <td>{spineAlign?.R_ROM != null ? spineAlign.R_ROM : '-'}</td>
                        </tr>
                        <tr>
                            <td>Forward Head Carriage</td>
                            {showGradingColumns && (
                                <>
                                    <td>{normal}</td>
                                    <td>{mild}</td>
                                    <td>{moderate}</td>
                                    <td>{severe}</td>
                                    <td>{verySevere}</td>
                                </>
                            )}
                            <td></td>
                            <td>{forwardHead?.ROM != null ? forwardHead.ROM : '-'}</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td>Shoulders Rolled Forward</td>
                            {showGradingColumns && (
                                <>
                                    <td>{normal}</td>
                                    <td>{mild}</td>
                                    <td>{moderate}</td>
                                    <td>{severe}</td>
                                    <td>{verySevere}</td>
                                </>
                            )}
                            <td></td>
                            <td>{shouldersRolled?.ROM != null ? shouldersRolled.ROM : '-'}</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td>Shoulder Height Difference</td>
                            {showGradingColumns && (
                                <>
                                    <td>{normal}</td>
                                    <td>{mild}</td>
                                    <td>{moderate}</td>
                                    <td>{severe}</td>
                                    <td>{verySevere}</td>
                                </>
                            )}
                            <td></td>
                            <td>{shoulderHeightDiff?.ROM ? shoulderHeightDiff.ROM : '-'}</td>
                            <td></td>
                        </tr>
                        <tr className="section-divider">
                            <td colSpan={showGradingColumns ? "9" : "4"}><strong>DEVIATION FROM MIDLINE</strong></td>
                        </tr>
                        <tr>
                            <td>Knee</td>
                            {showGradingColumns && (
                                <>
                                    <td>{normal}</td>
                                    <td>{mild}</td>
                                    <td>{moderate}</td>
                                    <td>{severe}</td>
                                    <td>{verySevere}</td>
                                </>
                            )}
                            <td></td>
                            <td>{postureRomData?.KneeLeftDeviation != null ? postureRomData.KneeLeftDeviation.toFixed(2) : '-'}</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td>Hip</td>
                            {showGradingColumns && (
                                <>
                                    <td>{normal}</td>
                                    <td>{mild}</td>
                                    <td>{moderate}</td>
                                    <td>{severe}</td>
                                    <td>{verySevere}</td>
                                </>
                            )}
                            <td></td>
                            <td>{postureRomData?.HipLeftDeviation != null ? postureRomData.HipLeftDeviation.toFixed(2) : '-'}</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td>Shoulder</td>
                            {showGradingColumns && (
                                <>
                                    <td>{normal}</td>
                                    <td>{mild}</td>
                                    <td>{moderate}</td>
                                    <td>{severe}</td>
                                    <td>{verySevere}</td>
                                </>
                            )}
                            <td></td>
                            <td>{postureRomData?.ShoulderLeftDeviation != null ? postureRomData.ShoulderLeftDeviation.toFixed(2) : '-'}</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td>Nose</td>
                            {showGradingColumns && (
                                <>
                                    <td>{normal}</td>
                                    <td>{mild}</td>
                                    <td>{moderate}</td>
                                    <td>{severe}</td>
                                    <td>{verySevere}</td>
                                </>
                            )}
                            <td></td>
                            <td>{postureRomData?.Nose || '-'}</td>
                            <td></td>
                        </tr>
                        {/* SPINAL CURVES section - for ATHLETE tenant */}
                        {showGradingColumns && (
                            <>
                                <tr className="section-divider">
                                    <td colSpan={showGradingColumns ? "9" : "4"}><strong>SPINAL CURVES</strong></td>
                                </tr>
                                <tr>
                                    <td>Thoracic Kyphosis</td>
                                    {showGradingColumns && (
                                        <>
                                            {
                                                isSpinealignEnabled &&
                                                <>
                                                    <td>20°-50°</td>
                                                    <td>51°-54°</td>
                                                    <td>53°-55°</td>
                                                    <td>54°-57°</td>
                                                    <td>58° or greater</td>
                                                </>
                                            }
                                            {
                                                isAthleteEnabled &&
                                                <>
                                                    <td>32° or less</td>
                                                    <td>33°-36°</td>
                                                    <td>37°-41°</td>
                                                    <td>42°-44°</td>
                                                    <td>45° or greater</td>
                                                </>
                                            }
                                        </>
                                    )}
                                    <td></td>
                                    <td>{postureRomData?.ThoracicKyphosis || '-'}</td>
                                    <td></td>
                                </tr>
                            </>
                        )}
                        <tr className="section-divider">
                            <td colSpan="6"><strong>LUMBAR LORDOTIC CURVE</strong></td>
                            {showGradingColumns ? (
                                <>
                                    <td>Increased</td>
                                    <td>Normal</td>
                                    <td>Decreased</td>
                                </>
                            ) : (
                                <>
                                    <td>Increased</td>
                                    <td>Normal</td>
                                    <td>Decreased</td>
                                </>
                            )}
                        </tr>
                        <tr>
                            <td></td>
                            {showGradingColumns ? (
                                <>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                    <td className="pelvic-value">{postureRomData?.LumbarLordosis || '-'}</td>
                                    <td></td>
                                    <td></td>
                                </>
                            ) : (
                                <>
                                    <td className="pelvic-value">{postureRomData?.PelvicTiltAnterior ? postureRomData?.LumbarLordosis : ''}</td>
                                    <td>{postureRomData?.PelvicTiltNormal ? postureRomData?.LumbarLordosis : ''}</td>
                                    <td className="pelvic-value">{postureRomData?.PelvicTiltPosterior ? postureRomData?.LumbarLordosis : ''}</td>
                                </>
                            )}
                        </tr>
                        <tr className="section-divider">
                            <td colSpan="6"><strong>PELVIC TILT</strong></td>
                            {showGradingColumns ? (
                                <>
                                    <td>Anterior Pelvic Tilt</td>
                                    <td>Normal</td>
                                    <td>Posterior Pelvic Tilt</td>
                                </>
                            ) : (
                                <>
                                    <td>Anterior Pelvic Tilt</td>
                                    <td>Normal</td>
                                    <td>Posterior Pelvic Tilt</td>
                                </>
                            )}
                        </tr>
                        <tr>
                            <td></td>
                            {showGradingColumns ? (
                                <>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                    <td className="pelvic-value">{postureRomData?.PelvicTiltAnterior || '-'}</td>
                                    <td></td>
                                    <td></td>
                                </>
                            ) : (
                                <>
                                    <td className="pelvic-value">{postureRomData?.PelvicTiltAnterior || ''}</td>
                                    <td>{postureRomData?.PelvicTiltNormal || ''}</td>
                                    <td className="pelvic-value">{postureRomData?.PelvicTiltPosterior || ''}</td>
                                </>
                            )}
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    // Helper function to render athlete ROM table for a specific region
    const renderAthleteRomTable = (region) => {
        if (!region || !region.BodyPartList) return null;

        return (
            <table className="rom-table-athlete">
                <thead>
                    <tr>
                        <th colSpan="2" className="region-header">{region.BodyRegionName.toUpperCase()}</th>
                        <th colSpan="3" className="current-rom-header">CURRENT ROM: {AssessmentDate}</th>
                    </tr>
                </thead>
                <tbody>
                    {region.BodyPartList?.map((bodyPart, partIdx) => {
                        const validData = bodyPart.Data?.filter(m => {
                            const hasRomData = m.ROM != null || m.R_ROM != null || m.L_ROM != null;
                            return hasRomData;
                        }) || [];

                        if (validData.length === 0) return null;

                        return (
                            <React.Fragment key={partIdx}>
                                {/* Body Part Header Row */}
                                <tr className="body-part-header">
                                    <td>{bodyPart.BodyPartName}</td>
                                    <td>Normal Range</td>
                                    <td>L-ROM</td>
                                    <td>ROM</td>
                                    <td>R-ROM</td>
                                </tr>
                                {/* Data Rows */}
                                {validData.map((measurement, idx) => {
                                    // Helper to determine if value is below normal
                                    const isBelowNormal = (value) => {
                                        if (value == null || !measurement.Range) return false;
                                        const rangeParts = measurement.Range.split('-');
                                        const lowRange = parseFloat(rangeParts[0]);
                                        return value < lowRange;
                                    };

                                    return (
                                        <tr key={idx}>
                                            <td className="movement-name">{measurement.ReportGridTitle}</td>
                                            <td className="normal-range">
                                                {measurement.Range ? `${measurement.Range}º` : ''}
                                            </td>
                                            <td className={`measured-value ${isBelowNormal(measurement.L_ROM) ? 'below-normal' : ''}`}>
                                                {measurement.L_ROM != null ? measurement.L_ROM : ''}
                                            </td>
                                            <td className={`measured-value ${isBelowNormal(measurement.ROM) ? 'below-normal' : ''}`}>
                                                {measurement.ROM != null ? measurement.ROM : ''}
                                            </td>
                                            <td className={`measured-value ${isBelowNormal(measurement.R_ROM) ? 'below-normal' : ''}`}>
                                                {measurement.R_ROM != null ? measurement.R_ROM : ''}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        );
    };

    // Recursive helper for rendering hierarchical sections
    // Similar to C# PrintChildHtml helper - renders nested sections with increasing heading levels
    const renderHierarchicalSections = (sections, level = 2) => {
        if (!sections || !Array.isArray(sections) || sections.length === 0) {
            return null;
        }

        return sections.map((section, index) => {
            if (!section.MainContentText && !section.ChildList?.length) {
                return null;
            }

            const HeadingTag = `h${Math.min(level, 6)}`; // Cap at h6

            return (
                <div key={index} className={`hierarchical-section level-${level}`}>
                    {section.Title && (
                        <HeadingTag className="section-title">{section.Title}</HeadingTag>
                    )}
                    {section.MainContentText && (
                        <div
                            className="section-content"
                            dangerouslySetInnerHTML={{ __html: section.MainContentText }}
                        />
                    )}
                    {section.ChildList && section.ChildList.length > 0 && (
                        <div className="child-sections">
                            {renderHierarchicalSections(section.ChildList, level + 1)}
                        </div>
                    )}
                </div>
            );
        }).filter(Boolean);
    };

    // Helper function to render Risk Factor Counter Table by Region
    const renderRiskFactorCounterTable = () => {
        const bodyRegionData = RiskFactorData?.RiskFactoryByBodyRegionActivities;
        if (!bodyRegionData?.RiskFactoryByBodyRegionViewActivity || bodyRegionData.RiskFactoryByBodyRegionViewActivity.length === 0) {
            return null;
        }

        const regionData = bodyRegionData.RiskFactoryByBodyRegionViewActivity;

        // Use pre-calculated totals from the parent object
        const totals = {
            low: bodyRegionData.LowRiskCount || 0,
            moderate: bodyRegionData.ModerateRiskCount || 0,
            high: bodyRegionData.HighRiskCount || 0,
            total: bodyRegionData.TotalRiskCount || 0
        };

        return (
            <div className="risk-factor-counter-table-wrapper">
                <h3 className="counter-table-title">RISK FACTOR COUNTER TABLE</h3>
                <table className="risk-factor-counter-table">
                    <thead>
                        <tr>
                            <th>REGION</th>
                            <th># LOW RISK</th>
                            <th># MODERATE RISK</th>
                            <th># HIGH RISK</th>
                            <th>TOTAL POSITIVE RISK FACTORS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {regionData.map((region, index) => (
                            <tr key={index}>
                                <td>{region.BodyRegionName}</td>
                                <td>{region.LowRiskCount || 0}</td>
                                <td>{region.ModerateRiskCount || 0}</td>
                                <td>{region.HighRiskCount || 0}</td>
                                <td>{region.TotalRiskCount || 0}</td>
                            </tr>
                        ))}
                        <tr className="totals-row">
                            <td><strong>Total</strong></td>
                            <td><strong>{totals.low}</strong></td>
                            <td><strong>{totals.moderate}</strong></td>
                            <td><strong>{totals.high}</strong></td>
                            <td><strong>{totals.total}</strong></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    // Helper function to render Risk Factor Counter Tracker Chart (stacked bar)
    const renderRiskFactorTrackerChart = () => {
        if (!RiskFactorData?.RiskFactoryByCategoryActivities || RiskFactorData.RiskFactoryByCategoryActivities.length === 0) {
            return null;
        }

        // Get counts for each risk category
        let highCount = 0, moderateCount = 0, lowCount = 0;
        RiskFactorData.RiskFactoryByCategoryActivities.forEach(category => {
            const count = category.AssessmentActivities?.length || 0;
            if (category.RiskCategoryName === 'High Risk') highCount = count;
            else if (category.RiskCategoryName === 'Moderate Risk') moderateCount = count;
            else if (category.RiskCategoryName === 'Low Risk') lowCount = count;
        });

        const totalCount = highCount + moderateCount + lowCount;
        if (totalCount === 0) return null;

        // Calculate percentages for SVG
        const chartHeight = 400;
        const chartWidth = 80;
        const maxValue = Math.max(totalCount, 100); // Scale to at least 100

        const lowHeight = (lowCount / maxValue) * chartHeight;
        const moderateHeight = (moderateCount / maxValue) * chartHeight;
        const highHeight = (highCount / maxValue) * chartHeight;

        return (
            <div className="risk-factor-tracker-chart">
                <h3 className="chart-title">Risk Factor Counter Tracker</h3>
                <div className="chart-container">
                    <div className="chart-legend">
                        <span className="legend-item"><span className="legend-color high"></span> High</span>
                        <span className="legend-item"><span className="legend-color moderate"></span> Moderate</span>
                        <span className="legend-item"><span className="legend-color low"></span> Low</span>
                    </div>
                    <div className="chart-wrapper">
                        <div className="y-axis">
                            {[110, 100, 90, 80, 70, 60, 50, 40, 30, 20, 10, 0].map(val => (
                                <span key={val} className="y-tick">{val}</span>
                            ))}
                        </div>
                        <svg width={chartWidth + 40} height={chartHeight + 20} className="stacked-bar-chart">
                            {/* Y-axis label */}
                            <text x="-100" y="15" transform="rotate(-90)" className="y-axis-label" style={{ fontSize: '10px', fill: '#666' }}>
                                Injury Risk
                            </text>

                            {/* Stacked bars */}
                            <g transform={`translate(30, 0)`}>
                                {/* Low Risk (bottom - green) */}
                                <rect
                                    x="0"
                                    y={chartHeight - lowHeight}
                                    width={chartWidth}
                                    height={lowHeight}
                                    fill="#28a745"
                                />
                                {lowCount > 0 && (
                                    <text x={chartWidth / 2} y={chartHeight - lowHeight / 2 + 4} textAnchor="middle" fill="white" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                        {lowCount}
                                    </text>
                                )}

                                {/* Moderate Risk (middle - yellow) */}
                                <rect
                                    x="0"
                                    y={chartHeight - lowHeight - moderateHeight}
                                    width={chartWidth}
                                    height={moderateHeight}
                                    fill="#ffc107"
                                />
                                {moderateCount > 0 && (
                                    <text x={chartWidth / 2} y={chartHeight - lowHeight - moderateHeight / 2 + 4} textAnchor="middle" fill="#333" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                        {moderateCount}
                                    </text>
                                )}

                                {/* High Risk (top - red) */}
                                <rect
                                    x="0"
                                    y={chartHeight - lowHeight - moderateHeight - highHeight}
                                    width={chartWidth}
                                    height={highHeight}
                                    fill="#dc3545"
                                />
                                {highCount > 0 && (
                                    <text x={chartWidth / 2} y={chartHeight - lowHeight - moderateHeight - highHeight / 2 + 4} textAnchor="middle" fill="white" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                        {highCount}
                                    </text>
                                )}

                                {/* Total count on top */}
                                <text x={chartWidth / 2} y={chartHeight - lowHeight - moderateHeight - highHeight - 8} textAnchor="middle" style={{ fontSize: '11px', fontWeight: 'bold' }}>
                                    {totalCount}
                                </text>
                            </g>

                            {/* X-axis date label */}
                            <text x={chartWidth / 2 + 30} y={chartHeight + 15} textAnchor="middle" style={{ fontSize: '10px', fill: '#666' }}>
                                {AssessmentDate}
                            </text>
                        </svg>
                    </div>
                </div>
            </div>
        );
    };

    // Helper function to render Posture Grading Scale Reference Table (for VATB)
    const renderPostureGradingScaleReference = () => {
        if (tenantType !== 'vatb') return null;

        // Get grading scale values from JSON
        const gradingScale = PostureGradingScales?.[0] || {};
        const normal = gradingScale.Excellent || '0.50" or less';
        const mild = gradingScale.Good || '0.51" - 1.00';
        const moderate = gradingScale.Average || '1.01" - 1.50';
        const severe = gradingScale.Poor || '1.51" - 2.00"';
        const verySevere = gradingScale.VeryPoor || '2.01" or greater';

        // Spinal curves grading
        const spinalNormal = '32" or less';
        const spinalMild = '33" - 36"';
        const spinalModerate = '37" - 41"';
        const spinalSevere = '42" - 44"';
        const spinalVerySevere = '45" or greater';

        return (
            <div className="posture-grading-scale-reference">
                <table className="grading-scale-table">
                    <thead>
                        <tr>
                            <th colSpan="6" className="table-header">MYMEDICALHUB POSTURE GRADING SCALE</th>
                        </tr>
                        <tr>
                            <th>DESCRIPTION</th>
                            <th>NORMAL</th>
                            <th>MILD</th>
                            <th>MODERATE</th>
                            <th>SEVERE</th>
                            <th>VERY SEVERE</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                Forward Head Carriage<br />
                                Shoulders Rolled Forward<br />
                                Knee Deviation<br />
                                Hip Deviation<br />
                                Shoulder Deviation<br />
                                Nose Deviation
                            </td>
                            <td>{normal}</td>
                            <td>{mild}</td>
                            <td>{moderate}</td>
                            <td>{severe}</td>
                            <td>{verySevere}</td>
                        </tr>
                        <tr>
                            <td>
                                Thoracic Kyphosis<br />
                                Lumbar Lordosis
                            </td>
                            <td>{spinalNormal}</td>
                            <td>{spinalMild}</td>
                            <td>{spinalModerate}</td>
                            <td>{spinalSevere}</td>
                            <td>{spinalVerySevere}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    // Helper function to render BMI Section (for VATB)
    const renderVatbBmiSection = () => {
        if (!PatientInfo?.Height || !PatientInfo?.WeightLbs) return null;

        const heightInches = parseFloat(PatientInfo.Height);
        const weightLbs = parseFloat(PatientInfo.WeightLbs);

        if (isNaN(heightInches) || isNaN(weightLbs) || heightInches === 0) return null;

        const bmi = Math.round((weightLbs / (heightInches * heightInches)) * 703);

        return (
            <div className="bmi-section content-wrapper">
                <div className="bmi-card">
                    <div className="bmi-content">
                        <div className="bmi-label">
                            <strong>Body Mass Index (BMI)</strong>
                        </div>
                        <div className="bmi-value" style={{ color: config.primaryColor, fontSize: "28px" }}>
                            {bmi}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Helper function to render Current Deviation table (for VATB)
    const renderVatbCurrentDeviationTable = () => {
        // Use RomReportDataView for VATB which has complete posture data
        if (!RomDataView?.RomReportDataView) return null;

        // Find Posture region in RomReportDataView
        const postureRegion = RomDataView.RomReportDataView.find(r => r.BodyRegionName === 'Posture');

        // Get posture data from BodyPartList > Data
        const postureData = postureRegion?.BodyPartList?.[0]?.Data || [];

        // PostureRomDataView is nested inside RomDataView, not Message
        const postureRomData = RomDataView?.PostureRomDataView;

        const spineAlign = postureData.find(m => m.ReportGridTitle === 'Spine Align');
        const forwardHead = postureData.find(m => m.ReportGridTitle === 'Forward Head Carriage');
        const shouldersRolled = postureData.find(m => m.ReportGridTitle === 'Shoulders Rolled Forward');
        const shoulderHeightDiff = postureData.find(m => m.ReportGridTitle === 'Shoulder Height Difference');

        // Calculate Shoulder Height Difference if not found in postureData
        // Try to get from PostureRomDataView shoulder heights
        const shoulderHeightDiffValue = shoulderHeightDiff?.ROM

        // Get grading scale values from JSON (first scale is for deviations)
        const gradingScale = PostureGradingScales?.[0] || {};
        const normal = gradingScale.Excellent || '0.50" or less';
        const mild = gradingScale.Good || '0.51"-1.00"';
        const moderate = gradingScale.Average || '1.01"-1.50"';
        const severe = gradingScale.Poor || '1.51"-2.00"';
        const verySevere = gradingScale.VeryPoor || '2.01" or greater';

        // For vatb tenant, show only POSTURE and deviation columns
        const showGradingColumns = tenantType !== 'vatb';

        // Determine if we should use left or right deviation values based on spine align
        // If L_ROM > R_ROM, use left deviation values, otherwise use right
        const useLeftDeviation = (spineAlign?.L_ROM != null && spineAlign?.R_ROM != null)
            ? spineAlign.L_ROM > spineAlign.R_ROM
            : true; // default to left if not available

        // Helper function to format deviation values (handles both number and string types)
        const formatDeviation = (value) => {
            if (value == null) return '-';
            if (typeof value === 'number') return value.toFixed(2);
            return value; // string values already formatted
        };

        return (
            <div className="posture-grading-section content-wrapper">
                <table className="posture-grading-table">
                    <thead>
                        <tr>
                            {showGradingColumns && (
                                <th colSpan="6" className="grading-scale-header">POSTURE GRADING SCALE</th>
                            )}
                            <th colSpan={showGradingColumns ? "3" : "4"} className="current-deviation-header">CURRENT DEVIATION: {AssessmentDate}</th>
                        </tr>
                        <tr>
                            <th>POSTURE</th>
                            {showGradingColumns && (
                                <>
                                    <th>Normal</th>
                                    <th>Mild</th>
                                    <th>Moderate</th>
                                    <th>Severe</th>
                                    <th>Very Severe</th>
                                </>
                            )}
                            <th>L-Deviation</th>
                            <th>Deviation</th>
                            <th>R-Deviation</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Spine Align</td>
                            {showGradingColumns && (
                                <>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                </>
                            )}
                            <td>{spineAlign?.L_ROM != null ? spineAlign.L_ROM : '-'}</td>
                            <td></td>
                            <td>{spineAlign?.R_ROM != null ? spineAlign.R_ROM : '-'}</td>
                        </tr>
                        <tr>
                            <td>Forward Head Carriage</td>
                            {showGradingColumns && (
                                <>
                                    <td>{normal}</td>
                                    <td>{mild}</td>
                                    <td>{moderate}</td>
                                    <td>{severe}</td>
                                    <td>{verySevere}</td>
                                </>
                            )}
                            <td></td>
                            <td>{forwardHead?.ROM != null ? forwardHead.ROM : '-'}</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td>Shoulders Rolled Forward</td>
                            {showGradingColumns && (
                                <>
                                    <td>{normal}</td>
                                    <td>{mild}</td>
                                    <td>{moderate}</td>
                                    <td>{severe}</td>
                                    <td>{verySevere}</td>
                                </>
                            )}
                            <td></td>
                            <td>{shouldersRolled?.ROM != null ? shouldersRolled.ROM : '-'}</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td>Shoulder Height Difference</td>
                            {showGradingColumns && (
                                <>
                                    <td>{normal}</td>
                                    <td>{mild}</td>
                                    <td>{moderate}</td>
                                    <td>{severe}</td>
                                    <td>{verySevere}</td>
                                </>
                            )}
                            <td></td>
                            <td>{shoulderHeightDiffValue ? shoulderHeightDiffValue.toFixed(2) : '-'}</td>
                            <td></td>
                        </tr>
                        <tr className="section-divider">
                            <td colSpan={showGradingColumns ? "9" : "4"}><strong>DEVIATION FROM MIDLINE</strong></td>
                        </tr>
                        <tr>
                            <td>Knee</td>
                            {showGradingColumns && (
                                <>
                                    <td>{normal}</td>
                                    <td>{mild}</td>
                                    <td>{moderate}</td>
                                    <td>{severe}</td>
                                    <td>{verySevere}</td>
                                </>
                            )}
                            <td></td>
                            <td>{postureRomData?.KneeLeftDeviation != null ? postureRomData.KneeLeftDeviation.toFixed(2) : '-'}</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td>Hip</td>
                            {showGradingColumns && (
                                <>
                                    <td>{normal}</td>
                                    <td>{mild}</td>
                                    <td>{moderate}</td>
                                    <td>{severe}</td>
                                    <td>{verySevere}</td>
                                </>
                            )}
                            <td></td>
                            <td>{postureRomData?.HipLeftDeviation != null ? postureRomData.HipLeftDeviation.toFixed(2) : '-'}</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td>Shoulder</td>
                            {showGradingColumns && (
                                <>
                                    <td>{normal}</td>
                                    <td>{mild}</td>
                                    <td>{moderate}</td>
                                    <td>{severe}</td>
                                    <td>{verySevere}</td>
                                </>
                            )}
                            <td></td>
                            <td>{postureRomData?.ShoulderLeftDeviation != null ? postureRomData.ShoulderLeftDeviation.toFixed(2) : '-'}</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td>Nose</td>
                            {showGradingColumns && (
                                <>
                                    <td>{normal}</td>
                                    <td>{mild}</td>
                                    <td>{moderate}</td>
                                    <td>{severe}</td>
                                    <td>{verySevere}</td>
                                </>
                            )}
                            <td></td>
                            <td>{postureRomData?.Nose || '-'}</td>
                            <td></td>
                        </tr>
                        {/* SPINAL CURVES section - for VATB tenant */}
                        {!showGradingColumns && (
                            <>
                                <tr className="section-divider">
                                    <td colSpan="4"><strong>SPINAL CURVES</strong></td>
                                </tr>
                                <tr>
                                    <td>Thoracic Kyphosis</td>
                                    <td></td>
                                    <td>{postureRomData?.ThoracicKyphosis || '-'}</td>
                                    <td></td>
                                </tr>
                            </>
                        )}
                        <tr className="section-divider">
                            <td colSpan="6"><strong>LUMBAR LORDOTIC CURVE</strong></td>
                            {showGradingColumns ? (
                                <>
                                    <td>Increased</td>
                                    <td>Normal</td>
                                    <td>Decreased</td>
                                </>
                            ) : (
                                <>
                                    <td>Increased</td>
                                    <td>Normal</td>
                                    <td>Decreased</td>
                                </>
                            )}
                        </tr>
                        <tr>
                            <td></td>
                            {showGradingColumns ? (
                                <>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                    <td></td>
                                    <td>{postureRomData?.LumbarLordosis || '-'}</td>
                                    <td></td>
                                </>
                            ) : (
                                <>
                                    <td>{postureRomData?.PelvicTiltAnterior ? postureRomData?.LumbarLordosis : ''}</td>
                                    <td>{postureRomData?.PelvicTiltNormal ? postureRomData?.LumbarLordosis : ''}</td>
                                    <td>{postureRomData?.PelvicTiltPosterior ? postureRomData?.LumbarLordosis : ''}</td>
                                </>
                            )}
                        </tr>
                        <tr className="section-divider">
                            <td colSpan="6"><strong>PELVIC TILT</strong></td>
                            {showGradingColumns ? (
                                <>
                                    <td>Anterior Pelvic Tilt</td>
                                    <td>Normal</td>
                                    <td>Posterior Pelvic Tilt</td>
                                </>
                            ) : (
                                <>
                                    <td>Anterior Pelvic Tilt</td>
                                    <td>Normal</td>
                                    <td>Posterior Pelvic Tilt</td>
                                </>
                            )}
                        </tr>
                        <tr>
                            <td></td>
                            {showGradingColumns ? (
                                <>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                    <td className="pelvic-value">{postureRomData?.PelvicTiltAnterior || '-'}</td>
                                    <td></td>
                                    <td></td>
                                </>
                            ) : (
                                <>
                                    <td className="pelvic-value">{postureRomData?.PelvicTiltAnterior || ''}</td>
                                    <td>{postureRomData?.PelvicTiltNormal || ''}</td>
                                    <td className="pelvic-value">{postureRomData?.PelvicTiltPosterior || ''}</td>
                                </>
                            )}
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    // Helper function to render Deviation from Midline section (for VATB)
    const renderVatbDeviationFromMidline = () => {
        const postureData = Message?.PostureRomDataView;
        if (!postureData) return null;

        const formatValue = (val) => {
            if (val === null || val === undefined || val === '') return '-';
            const numVal = parseFloat(val);
            return isNaN(numVal) ? '-' : numVal.toFixed(2);
        };

        // Get knee deviation (use whichever is available or average)
        const getKneeValue = () => {
            const left = postureData.KneeLeftDeviation;
            const right = postureData.KneeRightDeviation;
            if (left !== null && left !== undefined && right !== null && right !== undefined) {
                return ((left + right) / 2).toFixed(2);
            }
            return formatValue(left !== null && left !== undefined ? left : right);
        };

        // Get hip deviation
        const getHipValue = () => {
            const left = postureData.HipLeftDeviation;
            const right = postureData.HipRightDeviation;
            if (left !== null && left !== undefined && right !== null && right !== undefined) {
                return ((left + right) / 2).toFixed(2);
            }
            return formatValue(left !== null && left !== undefined ? left : right);
        };

        // Get shoulder deviation
        const getShoulderValue = () => {
            const left = postureData.ShoulderLeftDeviation;
            const right = postureData.ShoulderRightDeviation;
            if (left !== null && left !== undefined && right !== null && right !== undefined) {
                return ((left + right) / 2).toFixed(2);
            }
            return formatValue(left !== null && left !== undefined ? left : right);
        };

        return (
            <div className="deviation-from-midline-vatb">
                <h3>DEVIATION FROM MIDLINE</h3>
                <div className="deviation-rows">
                    <div className="deviation-row">
                        <span className="label">Knee</span>
                        <span className="value">{getKneeValue()}</span>
                    </div>
                    <div className="deviation-row">
                        <span className="label">Hip</span>
                        <span className="value">{getHipValue()}</span>
                    </div>
                    <div className="deviation-row">
                        <span className="label">Shoulder</span>
                        <span className="value">{getShoulderValue()}</span>
                    </div>
                    <div className="deviation-row">
                        <span className="label">Nose</span>
                        <span className="value">{formatValue(postureData.Nose)}</span>
                    </div>
                </div>
            </div>
        );
    };

    // Helper function to render Spinal Curves section (for VATB)
    const renderVatbSpinalCurves = () => {
        const postureData = Message?.PostureRomDataView;
        if (!postureData) return null;

        const formatValue = (val) => {
            if (val === null || val === undefined || val === '') return null;
            const numVal = parseFloat(val);
            return isNaN(numVal) ? null : numVal.toFixed(2);
        };

        const thoracicKyphosis = formatValue(postureData.ThoracicKyphosis);
        const lumbarLordosis = formatValue(postureData.LumbarLordosis);

        // Don't render if no data
        if (!thoracicKyphosis && !lumbarLordosis) return null;

        return (
            <div className="spinal-curves-vatb">
                <h3>SPINAL CURVES</h3>
                <div className="curve-rows">
                    {thoracicKyphosis && (
                        <div className="curve-row">
                            <span className="label">Thoracic Kyphosis</span>
                            <span className="value">{thoracicKyphosis}</span>
                        </div>
                    )}
                    {lumbarLordosis && (
                        <div className="curve-row">
                            <span className="label">Lumbar Lordosis</span>
                            <span className="value">{lumbarLordosis}</span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Helper function to render Pelvic Tilt section (for VATB)
    const renderVatbPelvicTilt = () => {
        const postureData = Message?.PostureRomDataView;
        if (!postureData) return null;

        const formatValue = (val) => {
            if (val === null || val === undefined || val === '') return null;
            const numVal = parseFloat(val);
            return isNaN(numVal) ? null : numVal.toFixed(2);
        };

        // Determine which pelvic tilt type has a value
        let tiltType = '';
        let tiltValue = null;

        if (postureData.PelvicTiltAnterior) {
            tiltType = 'Anterior Pelvic Tilt';
            tiltValue = formatValue(postureData.PelvicTiltAnterior);
        } else if (postureData.PelvicTiltNormal) {
            tiltType = 'Normal Pelvic Tilt';
            tiltValue = formatValue(postureData.PelvicTiltNormal);
        } else if (postureData.PelvicTiltPosterior) {
            tiltType = 'Posterior Pelvic Tilt';
            tiltValue = formatValue(postureData.PelvicTiltPosterior);
        }

        // Don't render if no data
        if (!tiltType || !tiltValue) return null;

        return (
            <div className="pelvic-tilt-vatb">
                <h3>PELVIC TILT</h3>
                <div className="tilt-row">
                    <span className="label">{tiltType}</span>
                    <span className="value">{tiltValue}</span>
                </div>
            </div>
        );
    };

    // Helper function to render Postural Abnormalities Section (for VATB)
    const renderVatbPosturalAbnormalities = () => {
        if (!Message?.PosturalAbnormalitiesCurrent || Message.PosturalAbnormalitiesCurrent.length === 0) return null;

        const abnormalities = Message.PosturalAbnormalitiesCurrent;

        // Helper to find abnormality by name
        const findAbnormality = (name) => abnormalities.find(a => a.PostureName === name);

        // Define the pairings matching the CSHTML structure
        const pairings = [
            { left: findAbnormality('Forward Head'), right: findAbnormality('Retracted Head') },
            { left: findAbnormality('Rounded Shoulders'), right: findAbnormality('Retracted Shoulders') },
            { left: findAbnormality('Increased Kyphotic Curve'), right: findAbnormality('Anterior Pelvic Tilt') },
            { left: findAbnormality('Increased Lordotic Curve'), right: findAbnormality('Posterior Pelvic Tilt') },
            { left: findAbnormality('Decreased Lordotic Curve'), right: findAbnormality('Scoliosis') },
            { left: findAbnormality('Hip Hike'), right: null }
        ];

        return (
            <div className="postural-abnormalities-vatb content-wrapper">
                <h3>{isAthleteEnabled ? 'Postural Analysis' : 'POSTURAL ABNORMALITIES'}</h3>
                <p className="disclaimer-note">
                    {
                        isSpinealignEnabled ?
                            DisclaimerList.find(d => d.Id === 16)?.Description
                            :
                            DisclaimerList.find(d => d.Id === 14)?.Description
                    }
                </p>

                {
                    !isAthleteEnabled && <div className="assessment-header">
                        CURRENT: {AssessmentDate} ({Message?.TestId})
                    </div>
                }

                {isAthleteEnabled ? (
                    // Three column layout for athlete - text in column 1, radio buttons in column 2, Why It Matters in column 3
                    <div className="abnormalities-athlete-table">
                        {/* Table Header */}
                        <div className="abnormality-header-row">
                            <div className="abnormality-name-column">Posture Measurement</div>
                            <div className="abnormality-radio-column">Status</div>
                            <div className="abnormality-why-matters-column">Why it matters</div>
                        </div>
                        {/* Table Rows */}
                        {abnormalities.map((abnormality, idx) => (
                            <div key={idx} className="abnormality-row-athlete">
                                <div className="abnormality-name-column">
                                    <span className="finding-name">{abnormality.PostureName}</span>
                                </div>
                                <div className="abnormality-radio-column">
                                    <span className="response-options">
                                        <span className={`radio-option ${abnormality.Response?.toLowerCase() === 'no' ? 'selected' : ''}`}>
                                            <span className="radio-circle"></span> No
                                        </span>
                                        <span className={`radio-option ${abnormality.Response?.toLowerCase() === 'yes' ? 'selected yes-selected' : ''}`}>
                                            <span className="radio-circle"></span> Yes
                                        </span>
                                        {abnormality.PostureName === 'Hip Hike' ? (
                                            <>
                                                <span className={`radio-option ${abnormality.Response?.toLowerCase() === 'left' ? 'selected' : ''}`}>
                                                    <span className="radio-circle"></span> Left
                                                </span>
                                                <span className={`radio-option ${abnormality.Response?.toLowerCase() === 'right' ? 'selected' : ''}`}>
                                                    <span className="radio-circle"></span> Right
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <span className="radio-option-placeholder"></span>
                                                <span className="radio-option-placeholder"></span>
                                            </>
                                        )}
                                    </span>
                                </div>
                                <div className="abnormality-why-matters-column">
                                    {abnormality.WhyItMatters || abnormality.SportImpact || '-'}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    // Two column layout for non-athlete
                    <div className="abnormalities-two-column">
                        {pairings.map((pair, idx) => (
                            <div key={idx} className="abnormality-row">
                                {/* Left column item */}
                                {pair.left ? (
                                    <div className="abnormality-item">
                                        <span className="finding-name">{pair.left.PostureName}</span>
                                        <span className="response-options">
                                            <span className={`radio-option ${pair.left.Response?.toLowerCase() === 'no' ? 'selected' : ''}`}>
                                                <span className="radio-circle"></span> No
                                            </span>
                                            <span className={`radio-option ${pair.left.Response?.toLowerCase() === 'yes' ? 'selected yes-selected' : ''}`}>
                                                <span className="radio-circle"></span> Yes
                                            </span>
                                            {pair.left.PostureName === 'Hip Hike' ? (
                                                <>
                                                    <span className={`radio-option ${pair.left.Response?.toLowerCase() === 'left' ? 'selected' : ''}`}>
                                                        <span className="radio-circle"></span> Left
                                                    </span>
                                                    <span className={`radio-option ${pair.left.Response?.toLowerCase() === 'right' ? 'selected' : ''}`}>
                                                        <span className="radio-circle"></span> Right
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="radio-option-placeholder"></span>
                                                    <span className="radio-option-placeholder"></span>
                                                </>
                                            )}
                                        </span>
                                    </div>
                                ) : (
                                    <div className="abnormality-item empty-cell"></div>
                                )}
                                {/* Right column item */}
                                {pair.right ? (
                                    <div className="abnormality-item">
                                        <span className="finding-name">{pair.right.PostureName}</span>
                                        <span className="response-options">
                                            <span className={`radio-option ${pair.right.Response?.toLowerCase() === 'no' ? 'selected' : ''}`}>
                                                <span className="radio-circle"></span> No
                                            </span>
                                            <span className={`radio-option ${pair.right.Response?.toLowerCase() === 'yes' ? 'selected yes-selected' : ''}`}>
                                                <span className="radio-circle"></span> Yes
                                            </span>
                                        </span>
                                    </div>
                                ) : (
                                    <div className="abnormality-item empty-cell"></div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // Helper function to render ROM Section (for VATB)
    const renderVatbROMSection = () => {
        if (!RomDataView?.RomReportDataView || RomDataView.RomReportDataView.length === 0) return null;

        const getStatusClass = (romColor) => {
            if (romColor === -1) return 'status-abnormal';
            if (romColor === 0) return 'status-normal';
            if (romColor === 1) return 'status-warning';
            return 'status-none';
        };

        // Helper functions for athlete table formatting
        const shouldShowCenterDelta = (bodyPartName) => {
            return bodyPartName !== "Elbow" &&
                bodyPartName !== "Wrist" &&
                bodyPartName !== "Shoulder";
        };

        const formatDelta = (delta, unitName) => {
            if (delta === null || delta === undefined) return '-';
            const sign = delta > 0 ? '+' : '';
            return `${sign}${Math.round(delta)}${convertUnit(unitName)}`;
        };

        const convertUnit = (unitName) => {
            if (unitName === 'Degree') return '°';
            if (unitName === 'Inch' || unitName === 'inches') return '"';
            return unitName || '';
        };

        const renderRomValue = (value, unitName) => {
            if (value === null || value === undefined) return '';
            return `${Math.round(value)}${convertUnit(unitName)}`;
        };

        const renderStatusBadge = (status) => {
            if (!status || status === 'completed') return null;
            const statusLower = status.toLowerCase();

            // Define colors for specific statuses
            let color = '';
            if (statusLower === 'below') {
                color = '#ed6b75';
            } else if (statusLower === 'within') {
                color = '#27AE60';
            } else if (statusLower === 'above') {
                color = '#1176B4';
            }

            return (
                <span
                    className={`status-badge status-${statusLower}`}
                    style={{ color: color }}
                >
                    {status}
                </span>
            );
        };

        return (
            <div className="rom-section-vatb">
                <h2>RANGE OF MOTION</h2>

                <div className="status-legend">
                    <p>
                        Status legend: <strong className="status-below">Below</strong> = below typical range,
                        <strong className="status-within"> Within</strong> = within typical,
                        <strong className="status-above"> Above</strong> = above typical.
                        "Δ" shows the difference from typical (negative = less than typical; positive = greater).
                    </p>
                </div>

                {RomDataView.RomReportDataView
                    .filter(region => region.BodyRegionName?.toLowerCase() !== 'posture')
                    .map((region, regionIdx) => {
                        // Collect all images from this region
                        const allRegionImages = [];

                        // Use RomDataImageViewList for athlete/spinealign tenants
                        if (isAthleteEnabled || isSpinealignEnabled) {
                            const imageRegion = RomDataView?.RomDataImageViewList?.find(r => r.BodyRegionId === region.BodyRegionId);
                            imageRegion?.DataModel?.filter(d =>
                                d.RenderImageUrlCompress &&
                                d.IsDisplayImage &&
                                !d.RenderImageUrlCompress.includes('UnableToPerform')
                            ).forEach((data) => {
                                allRegionImages.push({
                                    url: data.RenderImageUrlCompress,
                                    caption: `${data.BodyPartTitle} ${data.ReportGridTitle}`,
                                    bodyPartName: data.BodyPartTitle,
                                    title: data.ReportGridTitle
                                });
                            });
                        } else {
                            // Use RomReportDataView for VATB and other tenants
                            region.BodyPartList?.forEach((bodyPart) => {
                                bodyPart.Data?.filter(d => d.RenderImageUrl && d.IsDisplayImage).forEach((data) => {
                                    allRegionImages.push({
                                        url: data.RenderImageUrl,
                                        caption: `${bodyPart.BodyPartName} ${data.ReportGridTitle}`,
                                        bodyPartName: bodyPart.BodyPartName,
                                        title: data.ReportGridTitle
                                    });
                                });
                            });
                        }

                        return (
                            <div key={regionIdx} className="rom-region">
                                <h3 className="region-heading">Region: {region.BodyRegionName}</h3>

                                {/* Render all movement images for this region in a single 2-column grid */}
                                {allRegionImages.length > 0 && (
                                    <div className="body-part-section">
                                        <div className="movement-images-grid">
                                            {allRegionImages.map((img, imgIdx) => (
                                                <div key={imgIdx} className="movement-image-item">
                                                    <p className="image-caption">
                                                        {img.caption}
                                                    </p>
                                                    <img
                                                        src={img.url}
                                                        alt={img.caption}
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Tables for body parts - conditional rendering based on tenant */}
                                <div className="rom-table-section">
                                    {(isAthleteEnabled || isSpinealignEnabled) ? (
                                        // Athlete/Spinealign: Separate table for each body part with body part name as header
                                        region.BodyPartList && region.BodyPartList.map((bodyPart, partIdx) => {
                                            const showCenterDelta = shouldShowCenterDelta(bodyPart.BodyPartName);
                                            const totalCols = showCenterDelta ? 8 : 7;
                                            return (
                                                <div key={partIdx} className="body-part-table-wrapper">
                                                    <p className="region-heading">{bodyPart.BodyPartName}</p>
                                                    <table className="rom-data-table athlete-rom-table">
                                                        <colgroup>
                                                            <col style={{ width: isFollowup && isAthleteEnabled ? '15%' : '18%' }} /> {/* Movement */}
                                                            <col style={{ width: isFollowup && isAthleteEnabled ? '10%' : '14%' }} /> {/* Your (L/R) */}
                                                            <col style={{ width: isFollowup && isAthleteEnabled ? '8%' : '10%' }} /> {/* Typical */}
                                                            <col style={{ width: '6%' }} />  {/* Δ L */}
                                                            {showCenterDelta && <col style={{ width: '6%' }} />}  {/* Δ (center) - conditional */}
                                                            <col style={{ width: '6%' }} />  {/* Δ R */}
                                                            <col style={{ width: isFollowup && isAthleteEnabled ? '10%' : '16%' }} /> {/* Status (L/R) */}
                                                            {isFollowup && isAthleteEnabled && <col style={{ width: '20%' }} />} {/* What Changed */}
                                                            <col style={{ width: isFollowup && isAthleteEnabled ? '15%' : '24%' }} /> {/* Why it matters */}
                                                        </colgroup>
                                                        <thead>
                                                            {/*<tr className="body-part-header-row">*/}
                                                            {/*    <th colSpan="3" className="body-part-name">{bodyPart.BodyPartName}</th>*/}
                                                            {/*    <th colSpan={totalCols - 3} className="current-rom-header">CURRENT ROM: {AssessmentDate}</th>*/}
                                                            {/*</tr>*/}
                                                            <tr className="column-header">
                                                                <th>Movement</th>
                                                                <th>Your (L/R)</th>
                                                                <th>Typical</th>
                                                                <th>Δ L</th>
                                                                {showCenterDelta && <th>Δ</th>}
                                                                <th>Δ R</th>
                                                                <th>Status (L/R)</th>
                                                                {isFollowup && isAthleteEnabled && <th>What Changed from Prior Assessment (L/R)</th>}
                                                                <th>Why it matters</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {/* Data rows for this body part */}
                                                            {bodyPart.Data && bodyPart.Data.map((data, dataIdx) => {
                                                                const lValue = renderRomValue(data.L_ROM, data.UnitName);
                                                                const cValue = renderRomValue(data.ROM, data.UnitName);
                                                                const rValue = renderRomValue(data.R_ROM, data.UnitName);

                                                                return (
                                                                    <tr key={dataIdx}>
                                                                        {/* Movement */}
                                                                        <td className="movement-name">{data.ReportGridTitle}</td>

                                                                        {/* Your (L/R) - Combined format */}
                                                                        <td className="your-values">
                                                                            <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', alignItems: 'center' }}>
                                                                                {lValue && <span>{lValue}</span>}
                                                                                {cValue && <span>{cValue}</span>}
                                                                                {(lValue || rValue) && <span className="separator">/</span>}
                                                                                {rValue && <span>{rValue}</span>}
                                                                                {!lValue && !cValue && !rValue && <span>-</span>}
                                                                            </div>
                                                                        </td>

                                                                        {/* Typical */}
                                                                        <td className="typical-range">{data.Range}{convertUnit(data.UnitName)}</td>

                                                                        {/* Δ L */}
                                                                        <td className="delta-left">{formatDelta(data.L_ROM_Delta, data.UnitName)}</td>

                                                                        {/* Δ (center) - Conditional */}
                                                                        {showCenterDelta && (
                                                                            <td className="delta-center">{formatDelta(data.ROM_Delta, data.UnitName)}</td>
                                                                        )}

                                                                        {/* Δ R */}
                                                                        <td className="delta-right">{formatDelta(data.R_ROM_Delta, data.UnitName)}</td>

                                                                        {/* Status (L/R) */}
                                                                        <td className="status-cell">
                                                                            <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                                                                                {renderStatusBadge(data.L_ROM_Status)}
                                                                                {renderStatusBadge(data.ROM_Status)}
                                                                                {(data.L_ROM_Status || data.R_ROM_Status) && !data.ROM_Status && <span className="separator">/</span>}
                                                                                {renderStatusBadge(data.R_ROM_Status)}
                                                                                {!data.L_ROM_Status && !data.ROM_Status && !data.R_ROM_Status && <span>-</span>}
                                                                            </div>
                                                                        </td>

                                                                        {/* What Changed from Prior Assessment */}
                                                                        {isFollowup && isAthleteEnabled && (
                                                                            <td className="changed-prior-assessment">
                                                                                {data.ChangedPriorAssessment || '-'}
                                                                            </td>
                                                                        )}

                                                                        {/* Why it matters */}
                                                                        <td className="why-matters">{data.WhyItMatters || '-'}</td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        // VATB: Single table with region name as header
                                        <table className="rom-data-table">
                                            <thead>
                                                <tr className="region-header-row">
                                                    <th colSpan="2" className="region-name">{region.BodyRegionName.toUpperCase()}</th>
                                                    <th colSpan="3" className="current-rom-header">CURRENT ROM: {AssessmentDate}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {region.BodyPartList && region.BodyPartList.map((bodyPart, partIdx) => (
                                                    <React.Fragment key={partIdx}>
                                                        {/* Body part header row */}
                                                        <tr className="body-part-header">
                                                            <td>{bodyPart.BodyPartName}</td>
                                                            <td>Normal Range</td>
                                                            <td>L-ROM</td>
                                                            <td>ROM</td>
                                                            <td>R-ROM</td>
                                                        </tr>
                                                        {/* Data rows for this body part */}
                                                        {bodyPart.Data && bodyPart.Data.map((data, dataIdx) => (
                                                            <tr key={dataIdx}>
                                                                <td className="movement-name">{data.ReportGridTitle}</td>
                                                                <td className="normal-range">{data.Range}º</td>
                                                                <td className={`measured-value ${getStatusClass(data.L_ROMColor)}`}>
                                                                    {data.L_ROM !== null && data.L_ROM !== undefined ? Math.round(data.L_ROM) : '-'}
                                                                </td>
                                                                <td className={`measured-value ${getStatusClass(data.ROMColor)}`}>
                                                                    {data.ROM !== null && data.ROM !== undefined ? Math.round(data.ROM) : '-'}
                                                                </td>
                                                                <td className={`measured-value ${getStatusClass(data.R_ROMColor)}`}>
                                                                    {data.R_ROM !== null && data.R_ROM !== undefined ? Math.round(data.R_ROM) : '-'}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </React.Fragment>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        );
                    })}
            </div>
        );
    };

    // Helper function to render Special Tests Section (for VATB)
    const renderVatbSpecialTestsSection = () => {
        if (!Message?.SpecialTests || Message.SpecialTests.length === 0) return null;

        const getMediaGridClass = (mediaCount) => {
            if (mediaCount === 1) return 'media-grid-single';
            if (mediaCount === 2) return 'media-grid-double';
            return 'media-grid-triple';
        };

        return (
            <div className="special-tests-section-vatb">
                <h2>SPECIAL TESTS</h2>

                {Message.SpecialTests.map((test, index) => (
                    <div key={index} className="special-test-card">
                        <div className="test-content-wrapper">
                            <div className="test-left-section">
                                <h3 className="test-name">{test.Name}</h3>

                                {test.SpecialTestMedia && test.SpecialTestMedia.length > 0 && (
                                    <div className={`test-media-grid ${getMediaGridClass(test.SpecialTestMedia.length)}`}>
                                        {test.SpecialTestMedia.map((media, mediaIdx) => (
                                            <div key={mediaIdx} className="media-item">
                                                <img
                                                    src={media.ImageUrl}
                                                    alt={`${test.Name} - Image ${mediaIdx + 1}`}
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="test-right-section">
                                <div className="response-indicators">
                                    {test.IsSuccessList && test.IsSuccessList.map((option, optIdx) => {
                                        const isSelected = test.SelectedIsSuccessList === option.Value;
                                        const isPositive = option.Text.toLowerCase() === 'positive';

                                        return (
                                            <div key={optIdx} className={`radio-option ${isSelected ? 'selected' : ''} ${isPositive && isSelected ? 'positive-selected' : ''}`}>
                                                <span className="radio-circle"></span>
                                                <span className="radio-label">{option.Text}</span>
                                            </div>
                                        );
                                    })}
                                </div>

                                {test.ClinicalObservation && (
                                    <div className="clinical-observation">
                                        <h4>Clinical Observation</h4>
                                        <p>{test.ClinicalObservation}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // Handler to send question to Ask EMMA chat
    const handleAskEmmaQuestion = (question) => {
        askEmmaQuestion(question);
    };

    // Helper function to render Significant Changes Section (for Athlete Follow-up)
    const renderSignificantChangesSection = () => {
        if (!isAthleteEnabled || !isFollowup || !AthleteSignificantChange) return null;

        return (
            <div className="significant-changes-section content-wrapper">
                <div className="section-heading">
                    <h2>V. SIGNIFICANT CHANGES</h2>
                </div>
                <div
                    className="narrative-content"
                    dangerouslySetInnerHTML={{ __html: AthleteSignificantChange }}
                />
            </div>
        );
    };

    // Helper to get Legal Disclaimer Title based on report type
    const getLegalDisclaimerTitle = () => {
        // IsPosture corresponds to SpineAlign tenant
        if (isSpinealignEnabled) {
            return 'III. LEGAL DISCLAIMER';
        }
        // Follow-up with Significant Changes (shifts subsequent sections)
        if (isFollowup && AthleteSignificantChange) {
            return 'VII. LEGAL DISCLAIMER';
        }
        // Default Standard/Initial
        return 'VI. LEGAL DISCLAIMER';
    };

    return (
        <Modal isOpen={open} className={`modal-lg report-json-viewer ${getFullScreenModalStyle()}`}>
            <ModalHeader>
                <div className='report-modal-header'>
                    {/*Report &nbsp;&nbsp;*/}
                    {modalFullScreen && (
                        <Fragment>
                            <a rel="noopener noreferrer" href={"!#"}
                                className="modal-action-btn"
                                onClick={onClickToggleModalScreenSize}
                                id="btnMinimize">
                                <span>
                                    <i className={modalFullScreen ? "fa fa-compress" : "fa fa-expand"} />
                                </span>
                            </a>
                            <button
                                className="download-report-btn"
                                onClick={handleDownloadReport}
                                disabled={isDownloading}
                            >
                                {isDownloading ? (
                                    <p style={{ display: 'flex !important', alignItems: 'center', justifyContent: 'center', margin: 0 }}>
                                        <i className="fa fa-spinner fa-spin mr-1" />
                                        Processing...
                                    </p>
                                ) : (
                                    'Download Report'
                                )}
                            </button>
                            <button
                                className="report-modal-close-btn"
                                onClick={toggleModal}
                            >
                                <i className="fa fa-times" /> Close
                            </button>
                            <Tooltip
                                placement="bottom"
                                target="btnMinimize">
                                MINIMIZE SCREEN
                            </Tooltip>
                        </Fragment>
                    )}
                    {!modalFullScreen && (
                        <Fragment>
                            <a rel="noopener noreferrer" href={"!#"}
                                className="modal-action-btn"
                                onClick={onClickToggleModalScreenSize}
                                id="btnMaximize">
                                <span>
                                    <i className={modalFullScreen ? "fa fa-compress" : "fa fa-expand"} />
                                </span>
                            </a>
                            <button
                                className="download-report-btn"
                                onClick={handleDownloadReport}
                                disabled={isDownloading}
                            >
                                {isDownloading ? (
                                    <p style={{ display: 'flex !important', alignItems: 'center', justifyContent: 'center', margin: 0 }}>
                                        <i className="fa fa-spinner fa-spin mr-1" />
                                        Processing...
                                    </p>
                                ) : (
                                    'Download Report'
                                )}
                            </button>
                            <button
                                className="report-modal-close-btn"
                                onClick={toggleModal}
                            >
                                <i className="fa fa-times" /> Close
                            </button>
                            <Tooltip
                                placement="bottom"
                                target="btnMaximize">
                                FULL SCREEN
                            </Tooltip>
                        </Fragment>
                    )}
                </div>
            </ModalHeader>
            <ModalBody>
                {isLoading ? (
                    <div className="report-loading-container">
                        <div className="report-spinner" aria-hidden="true" />
                        <p className="loading-text">Loading report data...</p>
                    </div>
                ) : (
                    <div className={`report-json-content tenant-${tenantType}`}>
                        {/* Tenant-Specific Header */}
                        <div className="report-header-top">
                            <div className="header-left">
                                <h1>{config.brandName}</h1>
                                <p>{config.brandTagline}</p>
                            </div>
                            <div className="header-right">
                                <div className="report-id"><strong>Report ID:</strong> {Message?.TestId || 'N/A'}</div>
                                <div className="generated-date"><strong>Generated:</strong> {new Date().toISOString().split('T')[0]}</div>
                            </div>
                        </div>

                        {/* Report Heading */}
                        {displayReportHeading && (
                            <div className="report-title">
                                <h1>{displayReportHeading}</h1>
                            </div>
                        )}

                        {/* Patient Information */}
                        {PatientInfo && (
                            <div className="patient-info">
                                <div className="section-heading">
                                    <h2>{config.profileSectionTitle}</h2>
                                </div>
                                <div className="athlete-profile-grid">
                                    {PatientInfo.FullName && (
                                        <div className="profile-item">
                                            <div className="label">{config.patientLabel}</div>
                                            <div className="value">{PatientInfo.FullName}</div>
                                        </div>
                                    )}
                                    {PatientInfo.DateOfBirth && (
                                        <div className="profile-item">
                                            <div className="label">DATE OF BIRTH</div>
                                            <div className="value">{PatientInfo.DateOfBirth}</div>
                                        </div>
                                    )}
                                    {AssessmentType && (
                                        <div className="profile-item">
                                            <div className="label">ASSESSMENT TYPE</div>
                                            <div className="value">{AssessmentType}</div>
                                        </div>
                                    )}
                                    {AssessmentDate && (
                                        <div className="profile-item">
                                            <div className="label">EXAM DATE</div>
                                            <div className="value">{AssessmentDate}</div>
                                        </div>
                                    )}
                                    <div className="profile-item">
                                        <div className="label">GENDER</div>
                                        <div className="value">{PatientInfo.Gender || (Message?.StrengthReport?.[0]?.GenderName) || 'N/A'}</div>
                                    </div>
                                    {PatientInfo.Height && (
                                        <div className="profile-item">
                                            <div className="label">HEIGHT</div>
                                            <div className="value">
                                                {(() => {
                                                    const heightInches = parseInt(PatientInfo.Height);
                                                    if (!isNaN(heightInches)) {
                                                        const feet = Math.floor(heightInches / 12);
                                                        const inches = heightInches % 12;
                                                        return `${feet}'${inches}"`;
                                                    }
                                                    return PatientInfo.Height;
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                    {PatientInfo.WeightLbs && (
                                        <div className="profile-item">
                                            <div className="label">WEIGHT</div>
                                            <div className="value">{PatientInfo.WeightLbs} lbs</div>
                                        </div>
                                    )}
                                    {config.showOccupation && PatientInfo.Occupation && (
                                        <div className="profile-item">
                                            <div className="label">OCCUPATION</div>
                                            <div className="value">{PatientInfo.Occupation}</div>
                                        </div>
                                    )}
                                    {isAthleteEnabled && PatientInfo.PrimaryPosition && (
                                        <div className="profile-item">
                                            <div className="label">PRIMARY POSITION</div>
                                            <div className="value">{PatientInfo.PrimaryPosition}</div>
                                        </div>
                                    )}
                                    {isAthleteEnabled && PatientInfo.CompetitiveLevel && (
                                        <div className="profile-item">
                                            <div className="label">COMPETITIVE LEVEL</div>
                                            <div className="value">{PatientInfo.CompetitiveLevel}</div>
                                        </div>
                                    )}
                                    {isAthleteEnabled && PatientInfo.PrimaryPerformanceGoal && (
                                        <div className="profile-item">
                                            <div className="label">PERFORMANCE GOAL</div>
                                            <div className="value">{PatientInfo.PrimaryPerformanceGoal}</div>
                                        </div>
                                    )}
                                    {isAthleteEnabled && PatientInfo.SportsTypeName && (
                                        <div className="profile-item">
                                            <div className="label">SPORTS TYPE</div>
                                            <div className="value">{PatientInfo.SportsTypeName}</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Narratives */}
                        {Narratives && Narratives.length > 0 && (
                            <div className="narratives">
                                {/* ASSESSMENT SUMMARY section for vatb tenant */}
                                {tenantType === 'vatb' && (() => {
                                    const assessmentNarratives = getVatbAssessmentNarratives(Narratives);
                                    const subjectiveObjectiveNarratives = getVatbSubjectiveObjectiveNarratives(Narratives);

                                    return (
                                        <>
                                            {assessmentNarratives.length > 0 && (
                                                <div className="assessment-summary-section content-wrapper">
                                                    <div className="section-heading">
                                                        <h2>ASSESSMENT SUMMARY</h2>
                                                    </div>
                                                    {assessmentNarratives.map((narrative, index) => (
                                                        <div key={index} className="narrative-subsection"
                                                            dangerouslySetInnerHTML={{ __html: narrative.Content }}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                            {/* SUMMARY OF SUBJECTIVE AND OBJECTIVE FINDINGS section */}
                                            {(subjectiveObjectiveNarratives.length > 0 || (config.showPainAssessment && Message?.BodySvgImage && Message?.ReportedSymptoms?.length > 0) || PostureGradingScales?.length > 0 || RomDataView?.RomDataImageViewList?.length > 0) && (
                                                <div className="subjective-objective-section content-wrapper">
                                                    <div className="section-heading">
                                                        <h2>SUMMARY OF SUBJECTIVE AND OBJECTIVE FINDINGS</h2>
                                                    </div>
                                                    {subjectiveObjectiveNarratives.map((narrative, index) => (
                                                        <div key={index} className="narrative-subsection"
                                                            dangerouslySetInnerHTML={{ __html: narrative.Content }}
                                                        />
                                                    ))}

                                                    {/* Patient's Pain Assessment - inside SUMMARY section for vatb */}
                                                    {config.showPainAssessment && Message?.BodySvgImage && Message?.ReportedSymptoms && Message.ReportedSymptoms.length > 0 && (
                                                        <div className="pain-assessment-subsection">
                                                            <h3>Patient's Pain Assessment</h3>

                                                            {/* Pain Scale Legend */}
                                                            <div className="pain-legend">
                                                                <div className="legend-item">
                                                                    <span className="legend-dot mild-pain"></span>
                                                                    <span className="legend-label">Mild Pain (0-3)</span>
                                                                </div>
                                                                <div className="legend-item">
                                                                    <span className="legend-dot moderate-pain"></span>
                                                                    <span className="legend-label">Moderate Pain (4-7)</span>
                                                                </div>
                                                                <div className="legend-item">
                                                                    <span className="legend-dot severe-pain"></span>
                                                                    <span className="legend-label">Severe Pain (8-10)</span>
                                                                </div>
                                                            </div>

                                                            {/* Body Diagram SVG */}
                                                            <div className="body-diagram-container">
                                                                <div dangerouslySetInnerHTML={{ __html: Message.BodySvgImage }} />
                                                            </div>

                                                            {/* Reported Symptoms Table */}
                                                            <div className="reported-symptoms">
                                                                <h4>Reported Pain Locations & Severity</h4>
                                                                <table className="symptoms-table">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Body Location</th>
                                                                            <th>Pain Level (VAS 0-10)</th>
                                                                            <th>Severity Category</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {Message.ReportedSymptoms.map((symptom, index) => {
                                                                            const painValue = symptom.Response;
                                                                            let severityClass = '';
                                                                            let severityLabel = '';

                                                                            if (painValue >= 0 && painValue <= 3) {
                                                                                severityClass = 'mild-pain';
                                                                                severityLabel = 'Mild';
                                                                            } else if (painValue >= 4 && painValue <= 7) {
                                                                                severityClass = 'moderate-pain';
                                                                                severityLabel = 'Moderate';
                                                                            } else if (painValue >= 8 && painValue <= 10) {
                                                                                severityClass = 'severe-pain';
                                                                                severityLabel = 'Severe';
                                                                            }

                                                                            const locationMatch = symptom.Symptom.match(/^([^-]+)/);
                                                                            const location = locationMatch ? locationMatch[1].trim() : symptom.Symptom;

                                                                            return (
                                                                                <tr key={index}>
                                                                                    <td>{location}</td>
                                                                                    <td className="pain-value">{painValue}/10</td>
                                                                                    <td>
                                                                                        <span className={`severity-badge ${severityClass}`}>
                                                                                            {severityLabel}
                                                                                        </span>
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* REGION: Posture - inside SUMMARY section for vatb */}
                                                    {(PostureGradingScales?.length > 0 || RomDataView?.RomDataImageViewList?.length > 0) && (
                                                        <>
                                                            <h3 className="region-subheading">REGION: Posture</h3>
                                                            {/* Posture-related sections for VATB in correct PDF order */}
                                                            {renderPostureImages()}
                                                            {renderVatbCurrentDeviationTable()}

                                                            {/* Pelvic Tilt for VATB */}
                                                            {renderVatbPelvicTilt()}

                                                            {/* MyMedicalHub Posture Grading Scale Reference Table */}
                                                            {renderPostureGradingScaleReference()}

                                                            {/* BMI Section for VATB */}
                                                            {renderVatbBmiSection()}

                                                            {/* Postural Abnormalities for VATB */}
                                                            {renderVatbPosturalAbnormalities()}

                                                            {/* Range of Motion for VATB */}
                                                            {renderVatbROMSection()}

                                                            {/* Special Tests for VATB */}
                                                            {renderVatbSpecialTestsSection()}                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}

                                {/* Standard narrative rendering for other tenants */}
                                {tenantType !== 'vatb' && (() => {
                                    // For athlete/spinealign, we need special handling
                                    // Sections III, IV, V should appear AFTER section II (Movement Insights)
                                    // So we render other narratives here, and III/IV/V will be rendered after movement insights
                                    const athleteMainSections = [
                                        'AthleteGeneralFindings',           // III. KEY FINDINGS
                                        'AthleteSummaryPerformanceImpact',  // IV. SUMMARY & PERFORMANCE IMPACT
                                        'AthleteRecommendedTrainingProgram', // V/VI. RECOMMENDED EXERCISE PROGRAM
                                        'AthleteSignificantChange'          // V. SIGNIFICANT CHANGES (Handled separately)
                                    ];

                                    if (isAthleteEnabled || isSpinealignEnabled) {
                                        // For athlete/spinealign, only render non-main-section narratives here
                                        // Main sections (III, IV, V) will be rendered after Movement Insights
                                        return Narratives.filter(n => !athleteMainSections.includes(n.Title)).map((narrative, index) => {
                                            return narrative.Content && (
                                                <div key={index} className="narrative-section">
                                                    <h2>{getNarrativeSectionTitle(narrative.Title)}</h2>
                                                    <div
                                                        className="narrative-content"
                                                        dangerouslySetInnerHTML={{ __html: narrative.Content }}
                                                    />
                                                </div>
                                            );
                                        });
                                    }

                                    // For standard tenants, render all narratives in original order
                                    return Narratives.map((narrative, index) => {
                                        return narrative.Content && (
                                            <div key={index} className="narrative-section">
                                                <h2>{getNarrativeSectionTitle(narrative.Title)}</h2>
                                                <div
                                                    className="narrative-content"
                                                    dangerouslySetInnerHTML={{ __html: narrative.Content }}
                                                />
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        )}

                        {/* Body Diagram - Pain Assessment - Hidden for athlete and vatb tenants (vatb has it in SUMMARY section) */}
                        {tenantType !== 'vatb' && config.showPainAssessment && Message?.BodySvgImage && Message?.ReportedSymptoms && Message.ReportedSymptoms.length > 0 && (
                            <div className="body-diagram-section content-wrapper">
                                <div className="section-heading">
                                    <h2>PATIENT'S PAIN ASSESSMENT</h2>
                                </div>

                                {/* Pain Scale Legend */}
                                <div className="pain-legend">
                                    <div className="legend-item">
                                        <span className="legend-dot mild-pain"></span>
                                        <span className="legend-label">Mild Pain (0-3)</span>
                                    </div>
                                    <div className="legend-item">
                                        <span className="legend-dot moderate-pain"></span>
                                        <span className="legend-label">Moderate Pain (4-7)</span>
                                    </div>
                                    <div className="legend-item">
                                        <span className="legend-dot severe-pain"></span>
                                        <span className="legend-label">Severe Pain (8-10)</span>
                                    </div>
                                </div>

                                {/* Body Diagram SVG */}
                                <div className="body-diagram-container">
                                    <div dangerouslySetInnerHTML={{ __html: Message.BodySvgImage }} />
                                </div>

                                {/* Reported Symptoms Table */}
                                <div className="reported-symptoms">
                                    <h3>Reported Pain Locations & Severity</h3>
                                    <table className="symptoms-table">
                                        <thead>
                                            <tr>
                                                <th>Body Location</th>
                                                <th>Pain Level (VAS 0-10)</th>
                                                <th>Severity Category</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Message.ReportedSymptoms.map((symptom, index) => {
                                                const painValue = symptom.Response;
                                                let severityClass = '';
                                                let severityLabel = '';

                                                if (painValue >= 0 && painValue <= 3) {
                                                    severityClass = 'mild-pain';
                                                    severityLabel = 'Mild';
                                                } else if (painValue >= 4 && painValue <= 7) {
                                                    severityClass = 'moderate-pain';
                                                    severityLabel = 'Moderate';
                                                } else if (painValue >= 8 && painValue <= 10) {
                                                    severityClass = 'severe-pain';
                                                    severityLabel = 'Severe';
                                                }

                                                // Extract location name from symptom text
                                                const locationMatch = symptom.Symptom.match(/^([^-]+)/);
                                                const location = locationMatch ? locationMatch[1].trim() : symptom.Symptom;

                                                return (
                                                    <tr key={index}>
                                                        <td>{location}</td>
                                                        <td className="pain-value">{painValue}/10</td>
                                                        <td>
                                                            <span className={`severity-badge ${severityClass}`}>
                                                                {severityLabel}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Posture Grading Scales & ROM Section - Skip for vatb (it's in SUMMARY section) */}
                        {tenantType !== 'vatb' && (PostureGradingScales?.length > 0 || RomDataView?.RomDataImageViewList?.length > 0) && (
                            <>
                                {/* Section heading only for athlete tenant - matching _FullSingleReport.cshtml */}
                                {isAthleteEnabled && (
                                    <div className="section-heading">
                                        <h2>{config.movementSectionTitle}</h2>
                                    </div>
                                )}

                                {/* REGION: Posture - matching PDF structure */}
                                {isAthleteEnabled && (
                                    <h3 className="region-subheading">REGION: Posture</h3>
                                )}

                                {/* Posture Images Display - Front and Side Views with legends (matching PDF) */}
                                {isAthleteEnabled && RomDataView?.RomDataImageViewList && renderPostureImages()}

                                {/* Non-athlete posture images (original behavior) */}
                                {tenantType !== 'athlete' && tenantType !== 'vatb' && RomDataView?.RomDataImageViewList && renderPostureImages()}

                                {/* Posture Grading Scales - Athlete PDF Format */}
                                {isAthleteEnabled && RomDataView?.RomDataImageViewList && renderPostureGradingTable()}

                                {/* Non-athlete Posture Grading Scales (original format) */}
                                {tenantType !== 'athlete' && tenantType !== 'vatb' && PostureGradingScales && PostureGradingScales.length > 0 && (
                                    <div className="grading-scales content-wrapper">
                                        <h2>{Message.PostureGradingScaleTitle || "Posture Grading Scale"}</h2>
                                        {PostureGradingScales.map((scale, index) => (
                                            <div key={index} className="grading-scale-item">
                                                <h3>{scale.Name}</h3>
                                                <table>
                                                    <thead>
                                                        <tr>
                                                            <th>Excellent</th>
                                                            <th>Good</th>
                                                            <th>Average</th>
                                                            <th>Poor</th>
                                                            <th>Very Poor</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            <td>{scale.Excellent}</td>
                                                            <td>{scale.Good}</td>
                                                            <td>{scale.Average}</td>
                                                            <td>{scale.Poor}</td>
                                                            <td>{scale.VeryPoor}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* BMI Display - Athlete Format */}
                                {isAthleteEnabled && PatientInfo?.Height && PatientInfo?.WeightLbs && (
                                    <div className="bmi-display-box">
                                        {(() => {
                                            const heightInches = parseFloat(PatientInfo.Height);
                                            const weightLbs = parseFloat(PatientInfo.WeightLbs);
                                            if (heightInches && weightLbs) {
                                                const bmi = Math.round((weightLbs / (heightInches * heightInches)) * 703);
                                                return (
                                                    <span>Body Mass Index (BMI) <span className="bmi-value" style={{ color: config.primaryColor, fontSize: "20px" }}>{bmi}</span></span>
                                                );
                                            }
                                        })()}
                                    </div>
                                )}

                                {/* Postural Abnormalities Checklist - Athlete PDF Format */}
                                {isAthleteEnabled && Message?.PosturalAbnormalitiesCurrent && Message.PosturalAbnormalitiesCurrent.length > 0 && (
                                    <div className="postural-abnormalities-section">
                                        <h3>Postural Analysis</h3>
                                        <p className="disclaimer-note" style={{ fontStyle: 'italic', fontSize: '0.85em', color: '#666', marginBottom: '15px' }}>
                                            * {
                                                isSpinealignEnabled ?
                                                    DisclaimerList.find(d => d.Id === 16)?.Description
                                                    :
                                                    DisclaimerList.find(d => d.Id === 14)?.Description
                                            }
                                        </p>

                                        {/* Current Assessment Header */}
                                        {
                                            !isAthleteEnabled && <div className="assessment-header">
                                                CURRENT: {AssessmentDate} ({Message?.TestId})
                                            </div>
                                        }

                                        {/* Three column layout for athlete - text in column 1, radio buttons in column 2, Why It Matters in column 3 */}
                                        <div className="abnormalities-athlete-table">
                                            {/* Table Header */}
                                            <div className="abnormality-header-row">
                                                <div className="abnormality-name-column">Posture Measurement</div>
                                                <div className="abnormality-radio-column">Status</div>
                                                <div className="abnormality-why-matters-column">Why it matters</div>
                                            </div>
                                            {/* Table Rows */}
                                            {Message.PosturalAbnormalitiesCurrent.map((abnormality, idx) => (
                                                <div key={idx} className="abnormality-row-athlete">
                                                    <div className="abnormality-name-column">
                                                        <span className="finding-name">{abnormality.PostureName}</span>
                                                    </div>
                                                    <div className="abnormality-radio-column">
                                                        <span className="response-options">
                                                            <span className={`radio-option ${abnormality.Response?.toLowerCase() === 'no' ? 'selected' : ''}`}>
                                                                <span className="radio-circle"></span> No
                                                            </span>
                                                            <span className={`radio-option ${abnormality.Response?.toLowerCase() === 'yes' ? 'selected yes-selected' : ''}`}>
                                                                <span className="radio-circle"></span> Yes
                                                            </span>
                                                            {abnormality.PostureName === 'Hip Hike' ? (
                                                                <>
                                                                    <span className={`radio-option ${abnormality.Response?.toLowerCase() === 'left' ? 'selected' : ''}`}>
                                                                        <span className="radio-circle"></span> Left
                                                                    </span>
                                                                    <span className={`radio-option ${abnormality.Response?.toLowerCase() === 'right' ? 'selected' : ''}`}>
                                                                        <span className="radio-circle"></span> Right
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <span className="radio-option-placeholder"></span>
                                                                    <span className="radio-option-placeholder"></span>
                                                                </>
                                                            )}
                                                        </span>
                                                    </div>
                                                    <div className="abnormality-why-matters-column">
                                                        {abnormality.WhyItMatters || abnormality.SportImpact || '-'}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* ROM (Range of Motion) Data - Athlete Format */}
                                {(isAthleteEnabled || isSpinealignEnabled) && renderVatbROMSection()}

                                {/* Ask EMMA Questions - Range of Motion */}
                                {(isAthleteEnabled || isSpinealignEnabled) && renderAskEmmaQuestions(1129, "Range of Motion")}

                                {/* Strength Testing Scoring Section - Only for athlete tenant - Placed after ROM */}
                                {config.showStrengthTesting && Message?.StrengthReport && Message.StrengthReport.length > 0 && (
                                    <div className="strength-testing-section content-wrapper">
                                        <div className="section-heading">
                                            <h2>STRENGTH TESTING SCORING</h2>
                                        </div>

                                        {/* Summary Table */}
                                        <table className="strength-summary-table">
                                            <thead>
                                                <tr>
                                                    <th>EXERCISE NAME</th>
                                                    <th>RESULT</th>
                                                    <th>CATEGORY</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Message.StrengthReport.map((test, index) => (
                                                    <tr key={index}>
                                                        <td>{test.ReportGridTitle}</td>
                                                        <td>{test.RepetitionCount}</td>
                                                        <td>{test.ScoreStatus}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        {/* Individual Test Details with Distribution Charts */}
                                        <div className="strength-tests-grid">
                                            {Message.StrengthReport.map((test, index) => (
                                                <div key={index} className="strength-test-item">
                                                    <h3 className="test-name"><strong>Exercise Name:</strong> {test.ReportGridTitle}</h3>
                                                    <p><strong>Recorded Result:</strong> {test.RepetitionCount} reps</p>
                                                    <p><strong>Category:</strong> {test.ScoreStatus}</p>

                                                    {/* Distribution Chart - matching _StrengthGraph.cshtml */}
                                                    {test.StrengthNormId > 0 && (
                                                        <div className="distribution-chart">
                                                            <div className="chart-title" style={{ textAlign: 'center', fontWeight: '500', marginBottom: '10px' }}>
                                                                {test.GenderName} {test.ExerciseName || test.ReportGridTitle} Scores Distribution (Age {test.AgeGroupName})
                                                            </div>
                                                            <div className="chart-container">
                                                                <svg viewBox={`0 0 ${(isAthleteEnabled || isSpinealignEnabled) ? 3200 : 1600} ${(isAthleteEnabled || isSpinealignEnabled) ? 1200 : 600}`} className="bell-curve-svg">
                                                                    {/* Generate bell curve with Gaussian distribution */}
                                                                    {(() => {
                                                                        const mean = test.MeanValue;
                                                                        const stdDev = test.StdValue;
                                                                        const minVal = test.MinValue;
                                                                        const maxVal = test.MaxValue;
                                                                        const userX = test.RepetitionCount;

                                                                        // Chart dimensions - scale for athlete/spinealign tenants
                                                                        const scale = (isAthleteEnabled || isSpinealignEnabled) ? 2.0 : 1;
                                                                        const chartLeft = 40 * scale;
                                                                        const chartRight = 1550 * scale;
                                                                        const chartTop = 20 * scale;
                                                                        const chartBottom = 440 * scale;
                                                                        const chartWidth = chartRight - chartLeft;
                                                                        const chartHeight = chartBottom - chartTop;

                                                                        // Dynamic tick step based on range
                                                                        const range = maxVal - minVal;
                                                                        const tickStep = 2;

                                                                        // Generate bell curve points
                                                                        const generateBellCurve = () => {
                                                                            const points = [];
                                                                            const factor = 1 / (stdDev * Math.sqrt(2 * Math.PI));
                                                                            let maxY = 0;

                                                                            for (let x = minVal; x <= maxVal; x += 0.1) {
                                                                                const exponent = -0.5 * Math.pow((x - mean) / stdDev, 2);
                                                                                const y = factor * Math.exp(exponent);
                                                                                if (y > maxY) maxY = y;
                                                                                points.push({ x, y });
                                                                            }

                                                                            return { points, maxY };
                                                                        };

                                                                        const { points, maxY } = generateBellCurve();

                                                                        // Convert data point to SVG coordinates
                                                                        const toSvg = (x, y) => ({
                                                                            x: chartLeft + ((x - minVal) / (maxVal - minVal)) * chartWidth,
                                                                            y: chartBottom - (y / maxY) * chartHeight
                                                                        });

                                                                        // Get Y value at specific X on curve
                                                                        const getYAtX = (targetX) => {
                                                                            const exponent = -0.5 * Math.pow((targetX - mean) / stdDev, 2);
                                                                            return (1 / (stdDev * Math.sqrt(2 * Math.PI))) * Math.exp(exponent);
                                                                        };

                                                                        // Quartile boundaries
                                                                        const q1 = test.QuartileZoneValue1;
                                                                        const q2 = test.QuartileZoneValue2;
                                                                        const q3 = test.QuartileZoneValue3;

                                                                        // Generate area paths for each quartile
                                                                        const generateAreaPath = (startX, endX, color) => {
                                                                            const areaPoints = points.filter(p => p.x >= startX && p.x <= endX);
                                                                            if (areaPoints.length < 2) return null;

                                                                            let path = `M ${toSvg(areaPoints[0].x, 0).x} ${chartBottom}`;
                                                                            areaPoints.forEach(p => {
                                                                                const svgPoint = toSvg(p.x, p.y);
                                                                                path += ` L ${svgPoint.x} ${svgPoint.y}`;
                                                                            });
                                                                            path += ` L ${toSvg(areaPoints[areaPoints.length - 1].x, 0).x} ${chartBottom} Z`;

                                                                            return <path key={`area-${startX}-${endX}`} d={path} fill={color} />;
                                                                        };

                                                                        // Generate vertical grid lines
                                                                        const generateGridLines = () => {
                                                                            const lines = [];
                                                                            for (let val = minVal; val <= maxVal; val += tickStep) {
                                                                                const xPos = chartLeft + ((val - minVal) / (maxVal - minVal)) * chartWidth;
                                                                                lines.push(
                                                                                    <line
                                                                                        key={`grid-${val}`}
                                                                                        x1={xPos}
                                                                                        y1={chartTop}
                                                                                        x2={xPos}
                                                                                        y2={chartBottom}
                                                                                        stroke="#888"
                                                                                        strokeWidth="2"
                                                                                    />
                                                                                );
                                                                            }
                                                                            return lines;
                                                                        };

                                                                        // Generate curve outline
                                                                        const curvePath = points.map((p, i) => {
                                                                            const svgPoint = toSvg(p.x, p.y);
                                                                            return i === 0 ? `M ${svgPoint.x} ${svgPoint.y}` : `L ${svgPoint.x} ${svgPoint.y}`;
                                                                        }).join(' ');

                                                                        // User marker position
                                                                        const userY = getYAtX(userX);
                                                                        const userSvg = toSvg(userX, userY);

                                                                        return (
                                                                            <>
                                                                                {/* Quartile areas */}
                                                                                {generateAreaPath(minVal, q1, '#FF6B6B')}
                                                                                {generateAreaPath(q1 + 0.5, q2, '#FFA500')}
                                                                                {generateAreaPath(q2 + 0.5, q3, '#FFFF00')}
                                                                                {generateAreaPath(q3 + 0.5, maxVal, '#008000')}

                                                                                {/* Vertical grid lines */}
                                                                                {generateGridLines()}

                                                                                {/* X-axis */}
                                                                                <line x1={chartLeft} y1={chartBottom} x2={chartRight} y2={chartBottom} stroke="#333" strokeWidth="1" />

                                                                                {/* X-axis ticks and labels - dynamic step */}
                                                                                {Array.from({ length: Math.floor((maxVal - minVal) / tickStep) + 1 }, (_, i) => minVal + i * tickStep).map(val => {
                                                                                    const xPos = chartLeft + ((val - minVal) / (maxVal - minVal)) * chartWidth;
                                                                                    return (
                                                                                        <g key={`tick-${val}`}>
                                                                                            <line x1={xPos} y1={chartBottom} x2={xPos} y2={chartBottom + 5} stroke="#333" strokeWidth="1" />
                                                                                            <text x={xPos} y={chartBottom + 100 * scale} textAnchor="middle" fontSize={24 * scale} fill="#333">{val}</text>
                                                                                        </g>
                                                                                    );
                                                                                })}

                                                                                {/* User marker - cyan triangle pointing down with tip touching curve */}
                                                                                <polygon
                                                                                    points={`${userSvg.x},${userSvg.y} ${userSvg.x - 24},${userSvg.y - 45} ${userSvg.x + 24},${userSvg.y - 45}`}
                                                                                    fill="#00FFFF"
                                                                                    stroke="#0000FF"
                                                                                    strokeWidth="4"
                                                                                />

                                                                                {/* X-axis label - centered above ticks */}
                                                                                <text x={chartLeft + chartWidth / 2} y={chartBottom + 60 * scale} textAnchor="middle" fontSize={22 * scale} fontWeight="500" fill="#333">
                                                                                    Number of {test.ExerciseName || test.ReportGridTitle}
                                                                                </text>
                                                                            </>
                                                                        );
                                                                    })()}
                                                                </svg>



                                                                {/* Legend with reference colors */}
                                                                <div className="chart-legend" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px', marginTop: '10px' }}>
                                                                    <span className="legend-item" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                                        <span className="legend-color" style={{ display: 'inline-block', width: '20px', height: '12px', backgroundColor: '#FF6B6B' }}></span> Q1 ({test.QuartileZoneName1})
                                                                    </span>
                                                                    <span className="legend-item" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                                        <span className="legend-color" style={{ display: 'inline-block', width: '20px', height: '12px', backgroundColor: '#FFA500' }}></span> Q2 ({test.QuartileZoneName2})
                                                                    </span>
                                                                    <span className="legend-item" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                                        <span className="legend-color" style={{ display: 'inline-block', width: '20px', height: '12px', backgroundColor: '#FFFF00' }}></span> Q3 ({test.QuartileZoneName3})
                                                                    </span>
                                                                    <span className="legend-item" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                                        <span className="legend-color" style={{ display: 'inline-block', width: '20px', height: '12px', backgroundColor: '#008000' }}></span> Q4 ({test.QuartileZoneName4})
                                                                    </span>
                                                                    <span className="legend-item" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                                        <span className="legend-color" style={{ display: 'inline-block', width: '20px', height: '12px', backgroundColor: '#00FFFF', border: '1px solid #0000FF' }}></span> You Are Here
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* ROM (Range of Motion) Data - Non-Athlete Format */}
                                {tenantType !== 'athlete' && tenantType !== 'vatb' && RomDataView?.RomDataImageViewList && RomDataView.RomDataImageViewList.length > 0 && (
                                    <div className="rom-section content-wrapper">
                                        <h2>Range of Motion Assessment</h2>

                                        {RomDataView.RomDataImageViewList.map((region, regionIndex) => {
                                            // Skip posture region for ROM display
                                            if (region.BodyRegionId === 5) return null;

                                            // Filter out measurements that were unable to perform or have no data
                                            const validMeasurements = region.DataModel?.filter(m => {
                                                const hasRomData = m.ROM != null || m.R_ROM != null || m.L_ROM != null;
                                                const isUnableToPerform = m.IsUnableToPerformedExercise_ROM &&
                                                    m.IsUnableToPerformedExercise_L_ROM &&
                                                    m.IsUnableToPerformedExercise_R_ROM;
                                                return hasRomData && !isUnableToPerform;
                                            }) || [];

                                            if (validMeasurements.length === 0) return null;

                                            return (
                                                <div key={regionIndex} className="rom-region">
                                                    <h3 className="region-subheading">{region.BodyRegionName}</h3>

                                                    {/* Non-athlete table format */}
                                                    <table className="rom-table">
                                                        <thead>
                                                            <tr>
                                                                <th>Movement</th>
                                                                <th>Normal Range</th>
                                                                <th>Measured</th>
                                                                <th>Left</th>
                                                                <th>Right</th>
                                                                <th>Status</th>
                                                                {config.showWhyItMatters && <th>Why It Matters</th>}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {validMeasurements.map((measurement, idx) => {
                                                                const getStatusClass = (colorCode) => {
                                                                    if (colorCode === -1) return 'status-abnormal';
                                                                    if (colorCode === 0) return 'status-normal';
                                                                    if (colorCode === 1) return 'status-warning';
                                                                    if (colorCode === 2) return 'status-within';
                                                                    return 'status-none';
                                                                };

                                                                const getStatusText = (colorCode) => {
                                                                    if (colorCode === -1) return 'Abnormal';
                                                                    if (colorCode === 0) return 'Normal';
                                                                    if (colorCode === 1) return 'Warning';
                                                                    if (colorCode === 2) return 'Within Range';
                                                                    return '-';
                                                                };

                                                                const primaryStatus = measurement.ROM != null
                                                                    ? measurement.ROMColor
                                                                    : (measurement.R_ROM != null ? measurement.R_ROMColor : measurement.L_ROMColor);

                                                                return (
                                                                    <tr key={idx}>
                                                                        <td className="movement-name">{measurement.ReportGridTitle}</td>
                                                                        <td className="normal-range">
                                                                            {measurement.Range || `${measurement.LowRange}-${measurement.HighRange}`}
                                                                        </td>
                                                                        <td className={`measured-value ${getStatusClass(measurement.ROMColor)}`}>
                                                                            {measurement.ROM != null ? `${measurement.ROM}°` : '-'}
                                                                        </td>
                                                                        <td className={`measured-value ${getStatusClass(measurement.L_ROMColor)}`}>
                                                                            {measurement.L_ROM != null ? `${measurement.L_ROM}°` : '-'}
                                                                        </td>
                                                                        <td className={`measured-value ${getStatusClass(measurement.R_ROMColor)}`}>
                                                                            {measurement.R_ROM != null ? `${measurement.R_ROM}°` : '-'}
                                                                        </td>
                                                                        <td className={`status-indicator ${getStatusClass(primaryStatus)}`}>
                                                                            {getStatusText(primaryStatus)}
                                                                        </td>
                                                                        {config.showWhyItMatters && (
                                                                            <td className="why-it-matters">
                                                                                {measurement.WhyItMatters || measurement.SportImpact || '-'}
                                                                            </td>
                                                                        )}
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>

                                                    {/* ROM Movement Images - Show after table for non-athlete */}
                                                    {validMeasurements.some(m => m.RenderImageUrlCompress && !m.RenderImageUrlCompress.includes('UnableToPerform')) && (
                                                        <div className="rom-images-grid">
                                                            <h4>{region.BodyRegionName} - Movement Images</h4>
                                                            <div className="images-container">
                                                                {validMeasurements.map((measurement, imgIdx) => {
                                                                    if (!measurement.RenderImageUrlCompress || measurement.RenderImageUrlCompress.includes('UnableToPerform')) {
                                                                        return null;
                                                                    }

                                                                    return (
                                                                        <div key={imgIdx} className="rom-image-item">
                                                                            <div className="rom-image-wrapper">
                                                                                <img
                                                                                    src={measurement.RenderImageUrlCompress}
                                                                                    alt={measurement.ExerciseName || measurement.ReportGridTitle}
                                                                                    onError={(e) => {
                                                                                        e.target.style.display = 'none';
                                                                                        e.target.parentElement.innerHTML = '<div class="image-error">Image unavailable</div>';
                                                                                    }}
                                                                                />
                                                                            </div>
                                                                            <div className="rom-image-label">
                                                                                <div className="movement-title">{measurement.ReportGridTitle}</div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Posture-Specific Deviations Table */}
                                {tenantType !== 'vatb' && Message?.PostureRomDataView && (
                                    <div className="posture-deviation-section">
                                        <h3>Posture Analysis - Deviations from Midline</h3>
                                        <table className="posture-table">
                                            <thead>
                                                <tr>
                                                    <th>Measurement</th>
                                                    <th>Grading Scale</th>
                                                    <th>L-Deviation</th>
                                                    <th>Deviation</th>
                                                    <th>R-Deviation</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {/* Shoulder Deviation */}
                                                {(Message.PostureRomDataView.ShoulderLeftDeviation != null ||
                                                    Message.PostureRomDataView.ShoulderRightDeviation != null) && (
                                                        <tr>
                                                            <td className="measurement-name">Shoulder Height</td>
                                                            <td className="grading-scale">
                                                                0.50" or less (Normal)<br />
                                                                0.51"-1.00" (Mild)<br />
                                                                1.01"-1.50" (Moderate)<br />
                                                                1.51"-2.00" (Severe)
                                                            </td>
                                                            <td className="deviation-value">
                                                                {Message.PostureRomDataView.ShoulderLeftDeviation != null
                                                                    ? `${Message.PostureRomDataView.ShoulderLeftDeviation.toFixed(2)}"`
                                                                    : '-'}
                                                            </td>
                                                            <td className="deviation-value">-</td>
                                                            <td className="deviation-value">
                                                                {Message.PostureRomDataView.ShoulderRightDeviation != null
                                                                    ? `${Message.PostureRomDataView.ShoulderRightDeviation.toFixed(2)}"`
                                                                    : '-'}
                                                            </td>
                                                        </tr>
                                                    )}

                                                {/* Hip Deviation */}
                                                {(Message.PostureRomDataView.HipLeftDeviation != null ||
                                                    Message.PostureRomDataView.HipRightDeviation != null) && (
                                                        <tr>
                                                            <td className="measurement-name">Hip Height</td>
                                                            <td className="grading-scale">
                                                                0.50" or less (Normal)<br />
                                                                0.51"-1.00" (Mild)<br />
                                                                1.01"-1.50" (Moderate)<br />
                                                                1.51"-2.00" (Severe)
                                                            </td>
                                                            <td className="deviation-value">
                                                                {Message.PostureRomDataView.HipLeftDeviation != null
                                                                    ? `${Message.PostureRomDataView.HipLeftDeviation.toFixed(2)}"`
                                                                    : '-'}
                                                            </td>
                                                            <td className="deviation-value">-</td>
                                                            <td className="deviation-value">
                                                                {Message.PostureRomDataView.HipRightDeviation != null
                                                                    ? `${Message.PostureRomDataView.HipRightDeviation.toFixed(2)}"`
                                                                    : '-'}
                                                            </td>
                                                        </tr>
                                                    )}

                                                {/* Knee Deviation */}
                                                {(Message.PostureRomDataView.KneeLeftDeviation != null ||
                                                    Message.PostureRomDataView.KneeRightDeviation != null) && (
                                                        <tr>
                                                            <td className="measurement-name">Knee Height</td>
                                                            <td className="grading-scale">
                                                                0.50" or less (Normal)<br />
                                                                0.51"-1.00" (Mild)<br />
                                                                1.01"-1.50" (Moderate)<br />
                                                                1.51"-2.00" (Severe)
                                                            </td>
                                                            <td className="deviation-value">
                                                                {Message.PostureRomDataView.KneeLeftDeviation != null
                                                                    ? `${Message.PostureRomDataView.KneeLeftDeviation.toFixed(2)}"`
                                                                    : '-'}
                                                            </td>
                                                            <td className="deviation-value">-</td>
                                                            <td className="deviation-value">
                                                                {Message.PostureRomDataView.KneeRightDeviation != null
                                                                    ? `${Message.PostureRomDataView.KneeRightDeviation.toFixed(2)}"`
                                                                    : '-'}
                                                            </td>
                                                        </tr>
                                                    )}

                                                {/* Nose Deviation */}
                                                {Message.PostureRomDataView.Nose && (
                                                    <tr>
                                                        <td className="measurement-name">Nose</td>
                                                        <td className="grading-scale">
                                                            0.50" or less (Normal)<br />
                                                            0.51"-1.00" (Mild)<br />
                                                            1.01"-1.50" (Moderate)<br />
                                                            1.51"-2.00" (Severe)
                                                        </td>
                                                        <td className="deviation-value">-</td>
                                                        <td className="deviation-value">{Message.PostureRomDataView.Nose}"</td>
                                                        <td className="deviation-value">-</td>
                                                    </tr>
                                                )}

                                                {/* Thoracic Kyphosis */}
                                                {Message.PostureRomDataView.ThoracicKyphosis && (
                                                    <tr>
                                                        <td className="measurement-name">Thoracic Kyphosis</td>
                                                        <td className="grading-scale">
                                                            20°-50° (Normal)<br />
                                                            51°-54° (Mild)<br />
                                                            53°-55° (Moderate)<br />
                                                            54°-57° (Severe)
                                                        </td>
                                                        <td className="deviation-value">-</td>
                                                        <td className="deviation-value">{Message.PostureRomDataView.ThoracicKyphosis}°</td>
                                                        <td className="deviation-value">-</td>
                                                    </tr>
                                                )}

                                                {/* Pelvic Tilt */}
                                                {(Message.PostureRomDataView.PelvicTiltAnterior ||
                                                    Message.PostureRomDataView.PelvicTiltNormal ||
                                                    Message.PostureRomDataView.PelvicTiltPosterior) && (
                                                        <tr>
                                                            <td className="measurement-name" rowSpan="2">Pelvic Tilt</td>
                                                            <td className="grading-scale" rowSpan="2">Anterior / Normal / Posterior</td>
                                                            <td className="deviation-label">Anterior</td>
                                                            <td className="deviation-label">Normal</td>
                                                            <td className="deviation-label">Posterior</td>
                                                        </tr>
                                                    )}
                                                {(Message.PostureRomDataView.PelvicTiltAnterior ||
                                                    Message.PostureRomDataView.PelvicTiltNormal ||
                                                    Message.PostureRomDataView.PelvicTiltPosterior) && (
                                                        <tr>
                                                            <td className="deviation-value pelvic-tilt">
                                                                {Message.PostureRomDataView.PelvicTiltAnterior || '-'}
                                                            </td>
                                                            <td className="deviation-value pelvic-tilt">
                                                                {Message.PostureRomDataView.PelvicTiltNormal || '-'}
                                                            </td>
                                                            <td className="deviation-value pelvic-tilt">
                                                                {Message.PostureRomDataView.PelvicTiltPosterior || '-'}
                                                            </td>
                                                        </tr>
                                                    )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Non-athlete Postural Abnormalities (original format) */}
                                {tenantType !== 'athlete' && Message?.PosturalAbnormalitiesCurrent && Message.PosturalAbnormalitiesCurrent.length > 0 && (
                                    <div className="postural-abnormalities-section">
                                        <h3>Postural Abnormalities Checklist</h3>

                                        {/* BMI Display */}
                                        {PatientInfo?.Height && PatientInfo?.WeightLbs && (
                                            <div className="bmi-info">
                                                <div className="bmi-row">
                                                    <span className="label">HEIGHT:</span>
                                                    <span className="value">{PatientInfo.Height} inches</span>
                                                </div>
                                                <div className="bmi-row">
                                                    <span className="label">WEIGHT:</span>
                                                    <span className="value">{PatientInfo.WeightLbs} lbs</span>
                                                </div>
                                                {(() => {
                                                    const heightInches = parseFloat(PatientInfo.Height);
                                                    const weightLbs = parseFloat(PatientInfo.WeightLbs);
                                                    if (heightInches && weightLbs) {
                                                        const bmi = ((weightLbs / (heightInches * heightInches)) * 703).toFixed(1);
                                                        let bmiCategory = '';
                                                        if (bmi < 18.5) bmiCategory = 'Underweight';
                                                        else if (bmi >= 18.5 && bmi < 25) bmiCategory = 'Normal';
                                                        else if (bmi >= 25 && bmi < 30) bmiCategory = 'Overweight';
                                                        else bmiCategory = 'Obese';

                                                        return (
                                                            <div className="bmi-row">
                                                                <span className="label">BMI:</span>
                                                                <span className="value">
                                                                    {bmi} <span className="bmi-category">({bmiCategory})</span>
                                                                </span>
                                                            </div>
                                                        );
                                                    }
                                                })()}
                                            </div>
                                        )}

                                        {/* Postural Abnormalities Table */}
                                        <table className="abnormalities-table">
                                            <thead>
                                                <tr>
                                                    <th>Posture Finding</th>
                                                    <th>Response</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Message.PosturalAbnormalitiesCurrent.map((abnormality, index) => {
                                                    const response = abnormality.Response || '-';
                                                    const isYes = response.toLowerCase() === 'yes';
                                                    const isNo = response.toLowerCase() === 'no';

                                                    return (
                                                        <tr key={index}>
                                                            <td className="posture-finding">{abnormality.PostureName}</td>
                                                            <td className={`response-value ${isYes ? 'response-yes' : isNo ? 'response-no' : ''}`}>
                                                                {response}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Ask EMMA Quick Questions for Postural Abnormalities - Hidden for athlete tenant */}
                                {config.showAskEmma && Message?.PosturalAbnormalitiesCurrent && Message.PosturalAbnormalitiesCurrent.length > 0 && postureQuestions.length > 0 && (
                                    <div className="ask-emma-questions">
                                        <div className="questions-header">
                                            <h4>Ask EMMA — Postural Abnormalities</h4>
                                            <div className="header-separator"></div>
                                        </div>
                                        <div className="questions-pills">
                                            {postureQuestions.map((question, index) => (
                                                <button
                                                    key={index}
                                                    className="question-pill-btn"
                                                    onClick={() => handleAskEmmaQuestion(question)}
                                                >
                                                    {question}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Athlete/Spinealign Main Narrative Sections (III, IV, V) - rendered after Movement Insights */}
                        {(isAthleteEnabled || isSpinealignEnabled) && Narratives && (
                            <div className="athlete-main-narratives">
                                {/* III. KEY FINDINGS THAT MAY AFFECT PERFORMANCE */}
                                <div className="key-findings-main content-wrapper">
                                    <div className="section-heading">
                                        <h2>III. KEY FINDINGS THAT MAY AFFECT PERFORMANCE</h2>
                                    </div>
                                    {renderKeyFindingsSection()}
                                    {/* Ask EMMA Questions - Key Findings */}
                                    {renderAskEmmaQuestions(1130, "Key Findings Affecting Performance")}
                                </div>

                                {/* IV. SUMMARY & PERFORMANCE IMPACT */}
                                {renderNarrativeByTitle('AthleteSummaryPerformanceImpact')}
                                {/* Ask EMMA Questions - Summary & Performance Impact */}
                                {renderAskEmmaQuestions(1131, "Summary & Performance Impact")}

                                {/* V. SIGNIFICANT CHANGES (Follow-up only) */}
                                {renderSignificantChangesSection()}

                                {/* V/VI. RECOMMENDED EXERCISE PROGRAM */}
                                {renderNarrativeByTitle('AthleteRecommendedTrainingProgram')}
                                {/* Ask EMMA Questions - Recommended Exercise Program */}
                                {renderAskEmmaQuestions(1132, "Recommended Exercise Program")}
                            </div>
                        )}

                        {/* Risk Factors Section - Hidden for athlete tenant */}
                        {config.showRiskFactors && RiskFactorData?.RiskFactoryByCategoryActivities && RiskFactorData.RiskFactoryByCategoryActivities.length > 0 && (
                            <div className="risk-factors-section content-wrapper">
                                <div className="section-heading">
                                    <h2>RISK FACTORS</h2>
                                </div>

                                {/* Risk Factor Counter Table by Region */}
                                {renderRiskFactorCounterTable()}

                                {/* Individual Risk Factors Header */}
                                <h3 className="individual-risk-factors-title">INDIVIDUAL RISK FACTORS</h3>
                                <p className="risk-factors-description">
                                    The individual ATLAS risk factors are quantified and listed below. They were used to identify potential injury risks for each region of the body assessed and can be used individually, collectively, or in a combined manner to assist in identifying possible or existing injury risks and promoting overall musculoskeletal health and wellness
                                </p>

                                {/* Detailed Risk Factor Lists by Category */}
                                {RiskFactorData.RiskFactoryByCategoryActivities.map((category, categoryIndex) => {
                                    if (!category.AssessmentActivities || category.AssessmentActivities.length === 0) {
                                        return null;
                                    }

                                    let categoryClass = '';
                                    if (category.RiskCategoryName === 'High Risk') categoryClass = 'high-risk';
                                    else if (category.RiskCategoryName === 'Moderate Risk') categoryClass = 'moderate-risk';
                                    else if (category.RiskCategoryName === 'Low Risk') categoryClass = 'low-risk';

                                    return (
                                        <div key={categoryIndex} className={`risk-category ${categoryClass}`}>
                                            <h3 className="category-title">{category.RiskCategoryName}</h3>
                                            <ol className="risk-factors-list">
                                                {category.AssessmentActivities.map((activity, activityIndex) => (
                                                    <li key={activityIndex} className="risk-factor-item">
                                                        {activity.QTitle}
                                                        {activity.AnswerValue && (
                                                            <span className="answer-value"> ({activity.AnswerValue})</span>
                                                        )}
                                                    </li>
                                                ))}
                                            </ol>
                                        </div>
                                    );
                                })}

                                {/* Risk Factor Counter Tracker Chart */}
                                {renderRiskFactorTrackerChart()}
                            </div>
                        )}

                        {/* Evidence List (Care Classifications/Diagnoses) */}
                        {config.showEvidenceList && EvidenceData && EvidenceData.length > 0 && (
                            <div className="evidence-section content-wrapper">
                                <div className="section-heading">
                                    <h2>POSSIBLE CARE CLASSIFICATIONS</h2>
                                </div>
                                <div className="evidence-list">
                                    <ul>
                                        {EvidenceData.map((item, index) => (
                                            <li key={index} className="evidence-item">
                                                {item.ReportName}
                                                {item.IsDoctorVerified && (
                                                    <span className="verified-badge"> ✓ Verified</span>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                    {Message?.CareClassificationCautionaryNotice && (
                                        <div className="cautionary-notice">
                                            <p>{Message.CareClassificationCautionaryNotice}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* VATB Other Narratives: CARE PLAN OPTIONS, MODALITY OPTIONS, SHORT & LONG-TERM GOALS, ERGONOMIC SUGGESTIONS */}
                        {tenantType === 'vatb' && Narratives && Narratives.length > 0 && (() => {
                            const sortedNarratives = getSortedVatbNarratives(getVatbOtherNarratives(Narratives));

                            return sortedNarratives.map((narrative, index) => (
                                <div key={index} className="narrative-section content-wrapper">
                                    <div className="section-heading">
                                        <h2>{getNarrativeSectionTitle(narrative.Title)}</h2>
                                    </div>
                                    <div
                                        className="narrative-content"
                                        dangerouslySetInnerHTML={{ __html: narrative.Content }}
                                    />
                                </div>
                            ));
                        })()}

                        {/* Hierarchical Rule-Based Data Sections */}
                        {RuleBasedData && Array.isArray(RuleBasedData) && RuleBasedData.length > 0 && (
                            <div className="rule-based-section content-wrapper">
                                {RuleBasedData.map((ruleSection, index) => (
                                    <div key={index} className="rule-section-group">
                                        {ruleSection.ReportName && (
                                            <h1 className="rule-report-name">{ruleSection.ReportName}</h1>
                                        )}
                                        {ruleSection.SectionList && (
                                            <div className="rule-sections">
                                                {renderHierarchicalSections(ruleSection.SectionList, 2)}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Exercise List */}
                        {((ExerciseList && ExerciseList.length > 0) || (ExercisePlan && ExercisePlan.length > 0)) && (
                            <div className="exercise-section content-wrapper">
                                {/* Show header only if not already shown by AthleteRecommendedTrainingProgram narrative */}
                                {(!isAthleteEnabled || !Narratives?.find(n => n.Title === 'AthleteRecommendedTrainingProgram')) && (
                                    <div className="section-heading">
                                        <h2>{config.exerciseSectionTitle}</h2>
                                    </div>
                                )}
                                {(ExerciseList || ExercisePlan).map((exercise, index) => {
                                    const exerciseName = exercise.ExerciseName || exercise.Name || '';
                                    const phaseName = exercise.PhaseName || '';

                                    return (
                                        <div key={index} className="exercise-item">
                                            <h3 className="exercise-name">
                                                EXERCISE #{index + 1} {exerciseName.toUpperCase()}{phaseName ? ` (${phaseName.toUpperCase()})` : ''}
                                            </h3>

                                            {/* Safety Alert - Only for athlete tenant */}
                                            {config.showSafetyAlerts && exercise.SafetyAlert && (
                                                <div className="safety-alert exercise-safety">
                                                    <div className="alert-header">
                                                        <span className="alert-icon">⚠️</span>
                                                        <strong>Safety Alert</strong>
                                                    </div>
                                                    <div className="alert-content">
                                                        {Array.isArray(exercise.SafetyAlert) ? (
                                                            <ul>
                                                                {exercise.SafetyAlert.map((alert, alertIdx) => (
                                                                    <li key={alertIdx}>{alert}</li>
                                                                ))}
                                                            </ul>
                                                        ) : (
                                                            <p>{exercise.SafetyAlert}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Exercise Images Grid - support both Images array and single ImageUrl */}
                                            {((exercise.Images && exercise.Images.length > 0) || exercise.ImageUrl) && (
                                                <div className="exercise-images-grid">
                                                    {exercise.Images && exercise.Images.length > 0 ? (
                                                        exercise.Images.map((imgUrl, imgIdx) => (
                                                            <div key={imgIdx} className="exercise-image">
                                                                <img
                                                                    src={imgUrl}
                                                                    alt={`${exercise.ExerciseName || exercise.Name} - Step ${imgIdx + 1}`}
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                    }}
                                                                />
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="exercise-image">
                                                            <img
                                                                src={exercise.ImageUrl}
                                                                alt={exercise.ExerciseName || exercise.Name}
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Purpose and Justification - before Instructions */}
                                            {exercise.Purpose && (
                                                <div className="exercise-purpose">
                                                    <strong>Purpose:</strong> {exercise.Purpose}
                                                </div>
                                            )}
                                            {exercise.Justification && (
                                                <div className="exercise-justification">
                                                    <strong>Justification:</strong> {exercise.Justification}
                                                </div>
                                            )}

                                            {exercise.Instructions && (
                                                <div className="exercise-instructions">
                                                    <h4>INSTRUCTIONS</h4>
                                                    <div dangerouslySetInnerHTML={{ __html: exercise.Instructions }} />
                                                </div>
                                            )}

                                            {/* Exercise Details in Grid Layout */}
                                            {(exercise.Sets || exercise.Reps || exercise.Frequency || exercise.Duration ||
                                                exercise.SetInCount || exercise.RepetitionInCount || exercise.HoldInSeconds || exercise.FrequencyName || exercise.RPE) && (
                                                    <div className="exercise-details">
                                                        {(exercise.Sets || exercise.SetInCount) && (
                                                            <div className="detail-item">
                                                                <strong>Sets:</strong> {exercise.Sets || exercise.SetInCount}
                                                            </div>
                                                        )}
                                                        {(exercise.Reps || exercise.RepetitionInCount) && (
                                                            <div className="detail-item">
                                                                <strong>Reps:</strong> {exercise.Reps || exercise.RepetitionInCount}
                                                            </div>
                                                        )}
                                                        {exercise.HoldInSeconds > 0 && (
                                                            <div className="detail-item">
                                                                <strong>Hold:</strong> {exercise.HoldInSeconds} seconds
                                                            </div>
                                                        )}
                                                        {(exercise.Frequency || exercise.FrequencyName) && (
                                                            <div className="detail-item">
                                                                <strong>Frequency:</strong> {exercise.Frequency || exercise.FrequencyName}
                                                            </div>
                                                        )}
                                                        {exercise.Duration && (
                                                            <div className="detail-item">
                                                                <strong>Duration:</strong> {exercise.Duration}
                                                            </div>
                                                        )}
                                                        {/* RPE Rating - Only for athlete tenant */}
                                                        {config.showSafetyAlerts && exercise.RPE && (
                                                            <div className="detail-item rpe-rating">
                                                                <strong>RPE:</strong> {exercise.RPE}/10
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                            {/* Formatted exercise prescription for athlete (e.g., "3 sets x 10 reps each side @ RPE 3/10") */}
                                            {config.showSafetyAlerts && exercise.FormattedPrescription && (
                                                <div className="exercise-prescription">
                                                    <span className="prescription-text">{exercise.FormattedPrescription}</span>
                                                </div>
                                            )}

                                            {exercise.Notes && (
                                                <div className="exercise-notes">
                                                    <strong>Notes:</strong> {exercise.Notes}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Legal Disclaimer Section - Only for athlete tenant */}
                        {config.showLegalDisclaimer && DisclaimerList && DisclaimerList.length > 0 && (
                            <div className="legal-disclaimer-section content-wrapper">
                                <div className="section-heading">
                                    <h2><span>{getLegalDisclaimerTitle()}</span></h2>
                                </div>
                                <div className="disclaimer-content" style={{ padding: '20px', borderRadius: '5px', marginTop: '10px' }}>
                                    {(() => {
                                        const athleteDisclaimer = DisclaimerList.find(d => d.Id === 12);
                                        if (athleteDisclaimer && athleteDisclaimer.Description) {
                                            return (
                                                <p style={{ fontStyle: 'italic', margin: 0, lineHeight: '1.6' }}>
                                                    {athleteDisclaimer.Description}
                                                </p>
                                            );
                                        }
                                        return null;
                                    })()}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </ModalBody>
        </Modal>
    );
};

export default ReportJsonViewer;
